<template>
    <div class="my-2 space-y-10">
        <div class="flex flex-col bg-white">
            <div
                v-for="({ group, groupKey }) in visibleFieldsByGroup"
                :key="groupKey"
                class="group border border-b-0 border-slate-200 last:border-b first:rounded-tl first:rounded-tr last:rounded-bl last:rounded-br"
                :class="{
                    'active !border-b !border-indigo-500': openFieldKey === groupKey,
                }"
                :active="openFieldKey === groupKey"
            >
                <div
                    class="flex cursor-pointer px-4 py-3 hover:bg-slate-50 rounded"
                    @click="toggleGroup(groupKey)"
                >
                    <div class="flex w-6">
                        <AppIcon
                            :name="groups[groupKey].icon"
                            class="m-auto h-4 w-4 text-slate-500/75 group-hover:text-indigo-500 group-[.active]:text-indigo-500"
                        />
                    </div>
                    <div
                        class="grow pl-1.5 text-sm text-slate-600 font-semibold group-hover:text-indigo-500 group-[.active]:text-indigo-500"
                    >
                        {{ groups[groupKey].label }}
                    </div>
                    <div class="flex">
                        <div
                            v-if="group.simple_fields?.length > 0 && groupKey !== 'aggregation'"
                            class="group/filter mr-3 flex rounded border border-slate-200 bg-white px-2 py-0.5 hover:border-indigo-400 hover:text-indigo-500"
                            @click.stop="resetFilters($lodash.map(group.fields, 'key'))"
                        >
                            <AppIcon
                                name="xmark"
                                class="m-auto h-3 w-3 text-slate-500 group-hover/filter:text-indigo-500"
                            />
                            <span class="my-auto ml-1 text-xs leading-none">{{ group.simple_fields.length }}</span>
                        </div>
                        <AppIcon
                            name="chevron-down"
                            class="m-auto h-3 w-3 group-[.active]:rotate-180 group-[.active]:text-indigo-500"
                        />
                    </div>
                </div>

                <div v-show="openFieldKey === groupKey" class="border-t border-slate-100 px-5">
                    <div class="divide-y divide-slate-200">
                        <div
                            v-for="field in group.fields"
                            :key="`${fields[field.key].type}-${field.key}`"
                            :title="field.title"
                            class="pt-5 last:pb-5"
                        >
                            <!-- Field Label -->
                            <div
                                v-if="fields[field.key].type !== 'checkbox'"
                                class="mb-2.5 text-left text-sm font-medium leading-5 text-slate-500"
                            >
                                <div
                                    v-if="
                                        ['select-code-multiple', 'hashtags-input'].includes(fields[field.key].type) &&
                                        fields[field.key].model &&
                                        fields[field.key].model.length > 0
                                    "
                                    class="flex justify-between"
                                >
                                    <div class="my-auto flex leading-none">
                                        <div class="flex">
                                            <Tooltip v-if="fields[field.key].tooltip" class="my-auto mr-2 box-content">
                                                <template #trigger>
                                                    <AppIcon
                                                        class="h-3.5 w-3.5 text-slate-400 transition duration-200 hover:text-slate-500"
                                                        name="circle-info"
                                                    />
                                                </template>
                                                <template #default>
                                                    <div class="font-bold">
                                                        <h5 class="text-lg">{{ fields[field.key].tooltip.title }}</h5>
                                                        <div class="space-y-2">
                                                            <p
                                                                v-for="(paragraph, index) in fields[field.key].tooltip
                                                                    .content"
                                                                :key="`paragraph-${index}`"
                                                                class="text-xs text-slate-300"
                                                                :class="{
                                                                    [paragraph.class]: !!paragraph.class,
                                                                }"
                                                            >
                                                                {{ paragraph.text }}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </template>
                                            </Tooltip>
                                            <div>{{ fields[field.key].label }}</div>
                                        </div>

                                        <span class="ml-1">({{ fields[field.key].model.length }})</span>
                                    </div>

                                    <div
                                        v-if="fields[field.key].conjunction"
                                        class="flex rounded border border-slate-200 bg-white px-0.5 py-0.5 text-xs font-medium text-slate-600"
                                    >
                                        <div
                                            v-for="conjunctionOption in fields[field.key].conjunction.options"
                                            :key="conjunctionOption"
                                            class="cursor-pointer rounded px-2 uppercase"
                                            :class="{
                                                'bg-slate-200':
                                                    conjunctionOption ===
                                                    $lodash.get(filtering.current, fields[field.key].conjunction.key),
                                            }"
                                            @click="
                                                setSingleValue(fields[field.key].conjunction.key, conjunctionOption)
                                            "
                                        >
                                            {{ getFromDictionary(`codes.conjunction.${conjunctionOption}`, 'label') }}
                                        </div>
                                    </div>
                                </div>

                                <div v-else>
                                    <div class="flex">
                                        <Tooltip v-if="fields[field.key].tooltip" class="my-auto mr-2 box-content">
                                            <template #trigger>
                                                <AppIcon
                                                    class="h-3.5 w-3.5 text-slate-400 transition duration-200 hover:text-slate-500"
                                                    name="circle-info"
                                                />
                                            </template>
                                            <template #default>
                                                <div class="font-bold">
                                                    <h5 class="text-lg">{{ fields[field.key].tooltip.title }}</h5>
                                                    <div class="space-y-2">
                                                        <p
                                                            v-for="(paragraph, index) in fields[field.key].tooltip
                                                                .content"
                                                            :key="`paragraph-${index}`"
                                                            class="text-xs text-slate-300"
                                                            :class="{
                                                                [paragraph.class]: !!paragraph.class,
                                                            }"
                                                        >
                                                            {{ paragraph.text }}
                                                        </p>
                                                    </div>
                                                </div>
                                            </template>
                                        </Tooltip>

                                        <span class="text-sm text-slate-600">{{
                                            fields[field.key].label
                                        }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Field Body -->
                            <div :class="{ 'pointer-events-none opacity-30': !field.is_enabled }">

                                <!-- Range Slider -->
                                <div v-if="fields[field.key].type === 'slider-range'" class="mb-6">
                                    <div class="mt-5 flex h-10 space-x-0.5">
                                        <div
                                            v-for="(item, index) in barStyleCache[`${groupKey}-${field.key}`]?.values" :key="index"
                                            class="mt-auto w-[5px] rounded-tl rounded-tr"
                                            :style="item"
                                        ></div>
                                    </div>

                                    <vue-slider
                                        v-model="fields[field.key].model"
                                        :data="fields[field.key].data"
                                        :marks="fields[field.key].marks"
                                        :enable-cross="false"
                                        :lazy="true"
                                        :clickable="false"
                                        :tooltip="'none'"
                                        :fixed="false"
                                        :contained="false"
                                        :dot-options="dotOptionsForRange(field.key)"
                                        @change="changeListeners[field.key]"
                                        @dragging="(values) => throttledDrag(field.key, values)"
                                        @error="console.error"
                                        @drag-end="changeListeners[field.key]"
                                    >
                                    </vue-slider>

                                    <div class="mt-2 flex justify-between">
                                        <!-- Min Input -->
                                        <div class="relative w-full">
                                            <input
                                                :value="fields[field.key].inputMin"
                                                type="text"
                                                class="h-8 w-full border-slate-200 rounded px-2.5 py-1 text-sm"
                                                @input="onMinInput($event, field.key)"
                                                @blur="onMinBlur(field.key)"
                                            />

                                            <div v-if="fields[field.key].numberType === 'percent'" class="absolute top-[1px] right-[1px] px-2 h-[calc(100%-2px)] flex text-sm font-semibold pointer-events-none bg-white z-0 rounded">
                                                <span class="m-auto">%</span>
                                            </div>
                                        </div>

                                        <div class="flex px-3 text-slate-400">
                                            <span class="m-auto leading-none">-</span>
                                        </div>

                                        <!-- Max Input -->
                                        <div class="relative w-full group/infinity">
                                            <input
                                                :value="fields[field.key].inputMax"
                                                type="text"
                                                class="h-8 w-full border-slate-200 rounded px-2.5 py-1 text-sm"
                                                @focus="fields[field.key].isActive = true"
                                                @input="onMaxInput($event, field.key)"
                                                @blur="fields[field.key].isActive = false; onMaxBlur(field.key)"
                                            />

                                            <div v-if="fields[field.key].numberType === 'percent'" class="absolute top-[1px] right-[1px] px-2 h-[calc(100%-2px)] flex text-sm font-semibold pointer-events-none bg-white z-0 rounded">
                                                <span class="m-auto">%</span>
                                            </div>

                                            <div
                                                v-if="fields[field.key].model[1] >= fields[field.key].maxDisplayValue"
                                                class="
                                                    absolute top-[1px] right-[1px] px-2 h-[calc(100%-2px)] flex text-sm
                                                    font-semibold bg-white z-0 rounded
                                                "
                                            >
                                                <span class="m-auto">
                                                    <Tooltip placement="top">
                                                        <template #trigger>
                                                            <AppIcon
                                                                class="h-3.5 w-3.5 text-slate-400 transition duration-200 hover:text-slate-500"
                                                                name="circle-info"
                                                            />
                                                        </template>
                                                        <template #default>
                                                            <div class="font-bold">
                                                                <h5 class="text-lg text-white">Upper Bound Reached</h5>
                                                                <div class="space-y-2">
                                                                    <p
                                                                        class="text-xs text-slate-300"
                                                                    >
                                                                        The current value you have set for this filter reached the upper bound limit. This means that there will be no limitation, and the search will return creators no matter how high their number is for this metric.
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </template>
                                                    </Tooltip>
                                                </span>
                                            </div>

                                            <div
                                                v-if="!fields[field.key].isActive && fields[field.key].model[1] >= fields[field.key].maxDisplayValue"
                                                class="absolute inset-[1px] w-[calc(100%-2px)] h-[calc(100%-2px)] bg-white flex group-hover/infinity:opacity-0 group-hover/infinity:pointer-events-none z-10 rounded"
                                            >
                                                <span class="px-2.5 py-1 text-sm">{{ fields[field.key].maxInputLabel || 'Infinity' }}</span>
                                            </div>

                                            <!-- <div
                                                v-if="fields[field.key].model[1] >= fields[field.key].maxDisplayValue"
                                                class="absolute left-0 -bottom-5 h-5 w-full bg-slate-800 text-white rounded-bl rounded-br flex"
                                            >
                                                <span class="m-auto text-[11px] font-medium">Infinity</span>
                                            </div> -->
                                        </div>
                                    </div>
                                </div>

                                <!-- Single Slider -->
                                <div v-else-if="fields[field.key].type === 'slider-single'" class="mb-12">
                                    <vue-slider
                                        v-model="fields[field.key].model"
                                        :min="
                                            fields[field.key]?.slider?.min != null
                                                ? fields[field.key].slider.min
                                                : $lodash.get(filtering.options, `${field.key}.min`)
                                        "
                                        :max="
                                            fields[field.key]?.slider?.max
                                                ? fields[field.key].slider.max
                                                : $lodash.get(filtering.options, `${field.key}.max`)
                                        "
                                        :enable-cross="false"
                                        :tooltip="'always'"
                                        :tooltip-placement="'bottom'"
                                        :tooltip-formatter="formatters[fields[field.key].numberType]"
                                        :interval="fields[field.key].numberType === 'percent' ? 0.01 : 1"
                                        :contained="false"
                                        :clickable="false"
                                        @change="changeListeners[field.key]"
                                    />
                                </div>

                                <!-- Multiselect -->
                                <div v-else-if="fields[field.key].type === 'select-code-multiple'" class="mb-5">
                                    <CodeSelect
                                        :ref="`codeselect-${field.key}`"
                                        :open="openSubDropdownKey === field.key"
                                        :id-field="fields[field.key].idField"
                                        :input-type="fields[field.key].type"
                                        :selected-options="fields[field.key].model"
                                        :title="fields[field.key].label"
                                        :code-type="fields[field.key].codeType"
                                        :platform="fields[field.key].platformId || platformId"
                                        :preload="true"
                                        :max-items="5000"
                                        :search-placeholder="fields[field.key].placeholder"
                                        :no-results-text="fields[field.key].noResultsText"
                                        :slider-options="fields[field.key].sliderOptions"
                                        :display-options="
                                            fields[field.key].displayOptions ? fields[field.key].displayOptions() : null
                                        "
                                        :current-filters-context="filtering.current"
                                        :mode="fields[field.key].mode ?? 'multiple'"
                                        :disabled="fields[field.key].disabled ? fields[field.key].disabled() : false"
                                        @update:open="val => openSubDropdownKey = val ? field.key : null"
                                        @selection-change="changeListeners[field.key]"
                                    >
                                        <template #selected-items>
                                            <div v-if="fields[field.key].model?.length > 0" class="mb-1 flex flex-col">
                                                <div
                                                    v-for="selectedItem in fields[field.key].model"
                                                    :key="fields[field.key].codeType === 'game' ? `${selectedItem.id}-${selectedItem.metadata}` : selectedItem.id"
                                                    class="mb-1 flex justify-between overflow-hidden rounded flex-col bg-slate-800"
                                                    :class="{
                                                        'relative overflow-hidden': selectedItem.background_url,
                                                    }"
                                                >
                                                    <div class="flex">
                                                        <div
                                                            v-if="selectedItem.background_url"
                                                            class="absolute z-0 h-full w-full bg-cover bg-center opacity-10"
                                                            :style="{
                                                                'background-image': `url(${selectedItem.background_url})`,
                                                            }"
                                                        />

                                                        <div class="z-10 grow my-auto px-2.5 py-1.5 text-sm font-medium text-white">
                                                            <span>{{ getCodeOptionDisplayLabel(fields[field.key], selectedItem) }}</span>
                                                            <span v-if="selectedItem?.metadata?.release_year" class="ml-0.5"
                                                                >({{ selectedItem.metadata.release_year }})</span
                                                            >
                                                        </div>

                                                        <div
                                                            v-if="selectedItem.value2"
                                                            class="z-10 my-auto ml-auto line-clamp-1 px-2.5 py-1.5 text-sm font-medium text-white"
                                                        >
                                                            ({{ sliderOptions.tooltipFormatter(selectedItem.value2) }})
                                                        </div>

                                                        <div class="mr-1.5 flex">
                                                            <div
                                                                v-if="fields[field.key].specifics?.length > 0"
                                                                class="my-auto cursor-pointer rounded-sm p-1 text-white opacity-90 hover:bg-black hover:bg-opacity-20 hover:opacity-70"
                                                                @click="openAdvancedFiltersModal(selectedItem, field.key)"
                                                            >
                                                                <AppIcon name="gear" class="h-4 w-4" />
                                                            </div>

                                                                <div
                                                                    class="my-auto cursor-pointer rounded-sm p-1 text-white opacity-90 hover:bg-black hover:bg-opacity-20 hover:opacity-70"
                                                                    @click="onRemoveItemFromList(fields[field.key], selectedItem)"
                                                                >
                                                                    <AppIcon name="xmark" class="h-4 w-4" />
                                                                </div>
                                                            </div>

                                                            <!-- Removed legacy PopoverContent used for advanced requirements -->

                                                        </div>

                                                    <div v-if="activeSpecificsForItem(field.key, selectedItem)?.length > 0" class="flex-col px-2.5 -mt-2">
                                                        <div v-for="(specificItem) in activeSpecificsForItem(field.key, selectedItem)" :key="specificItem.key" class="last:pb-2">
                                                            <div v-if="selectedItem.sub_model?.[specificItem.key]?.min || selectedItem.sub_model?.[specificItem.key]?.max || (specificItem.type === 'boolean')" class="flex text-xs text-slate-100">
                                                                <div class="pl-2 pt-0.5 pr-1.5">
                                                                    <div class="border-l border-slate-300 h-1.5 w-1.5" />
                                                                    <div class="border-t border-slate-300 h-1 w-1.5" />
                                                                </div>

                                                                <div class="grow leading-0">
                                                                    <span class="underline">{{ specificItem.label }}</span>
                                                                    <span v-if="selectedItem.sub_model?.[specificItem.key]?.min != null && selectedItem.sub_model?.[specificItem.key]?.min !== '' && selectedItem.sub_model?.[specificItem.key]?.max != null && selectedItem.sub_model?.[specificItem.key]?.max !== ''">
                                                                        is between {{ selectedItem.sub_model?.[specificItem.key]?.min }} and {{ selectedItem.sub_model?.[specificItem.key]?.max }}
                                                                    </span>
                                                                    <span v-else-if="selectedItem.sub_model?.[specificItem.key]?.min != null && selectedItem.sub_model?.[specificItem.key]?.min !== ''">
                                                                        is at least {{ selectedItem.sub_model?.[specificItem.key]?.min }}
                                                                    </span>
                                                                    <span v-else-if="selectedItem.sub_model?.[specificItem.key]?.max != null && selectedItem.sub_model?.[specificItem.key]?.max !== ''">
                                                                        is no more than {{ selectedItem.sub_model?.[specificItem.key]?.max }}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </template>
                                    </CodeSelect>
                                </div>

                                <!-- Checkbox -->
                                <div v-else-if="fields[field.key].type === 'checkbox'">
                                    <div class="flex items-center pb-5">
                                        <input
                                            v-model="fields[field.key].model"
                                            type="checkbox"
                                            :true-value="true"
                                            :false-value="null"
                                            @change="changeListeners[field.key]"
                                        />
                                        <label class="ml-2 text-sm text-slate-600">{{ fields[field.key].label }}</label>
                                        <Tooltip v-if="fields[field.key].tooltip" class="ml-2 box-content">
                                            <template #trigger>
                                                <AppIcon
                                                    class="h-3.5 w-3.5 text-slate-400 transition duration-200 hover:text-slate-500"
                                                    name="circle-info"
                                                />
                                            </template>
                                            <template #default>
                                                <div class="font-bold">
                                                    <h5 class="text-lg">{{ fields[field.key].tooltip.title }}</h5>
                                                    <div class="space-y-2">
                                                        <p
                                                            v-for="(paragraph, index) in fields[field.key].tooltip.content"
                                                            :key="`paragraph-${index}`"
                                                            class="text-xs text-slate-300"
                                                            :class="{ [paragraph.class]: !!paragraph.class }"
                                                        >
                                                            {{ paragraph.text }}
                                                        </p>
                                                    </div>
                                                </div>
                                            </template>
                                        </Tooltip>
                                    </div>
                                </div>

                                <!-- Radio List -->
                                <div v-else-if="fields[field.key].type === 'radio-list'" class="mb-5 flex flex-col">
                                    <label
                                        v-for="option in fields[field.key].options"
                                        :key="option.id"
                                        class="-mx-2 mr-3 rounded px-2 py-0.5 text-sm text-slate-600 hover:bg-slate-100"
                                    >
                                        <input
                                            v-model="fields[field.key].model"
                                            type="radio"
                                            :name="field.key"
                                            :value="option.id"
                                            :checked="option.id === fields[field.key].model"
                                            @change="changeListeners[field.key]"
                                        />
                                        <span class="ml-1.5">{{ option.title }}</span>
                                    </label>
                                </div>

                                <!-- Checkbox for affinity -->
                                <div
                                    v-else-if="fields[field.key].type === 'checkbox-affinity'"
                                    class="mb-5 flex space-x-2"
                                >
                                    <label v-for="option in fields[field.key].options" :key="option.id" class="text-sm">
                                        <div
                                            class="cursor-pointer rounded p-2 px-2 py-0.5 uppercase"
                                            :class="[
                                                `${option.affinity.bgSecondaryColor} ${option.affinity.textColor}`,
                                                {
                                                    checked: option.id == fields[field.key].model,
                                                },
                                            ]"
                                            @click="setSingleValue(field.key, option.value)"
                                        >
                                            {{ option.affinity.letter }}+
                                        </div>
                                    </label>
                                </div>

                                <!-- Range Slider for affinity -->
                                <div v-else-if="fields[field.key].type === 'slider-affinity'" class="mb-12">
                                    <vue-slider
                                        v-model="$lodash.get(fields, field.key).model"
                                        :data="$lodash.get(fields, field.key).data"
                                        :marks="true"
                                        :enable-cross="false"
                                        :lazy="true"
                                        :tooltip="'none'"
                                        :absorb="true"
                                        :clickable="false"
                                        :interval="0.1"
                                        :min-range="0.1"
                                        :fixed="false"
                                        :contained="false"
                                        @change="changeListeners[field.key]"
                                    >
                                        <template #mark="{ pos, label, active }">
                                            <div
                                                class="absolute top-4 -translate-x-3 whitespace-nowrap text-xs"
                                                :style="{
                                                    left: `${pos}%`,
                                                }"
                                            >
                                                <div
                                                    class="cursor-pointer rounded p-2 px-2 py-0.5 uppercase"
                                                    :class="[
                                                        getGradeAndColorsFromPercent(label).textColor,
                                                        getGradeAndColorsFromPercent(label).bgPrimaryColor,
                                                        {
                                                            'opacity-25': !active,
                                                        },
                                                    ]"
                                                >
                                                    {{ getGradeAndColorsFromPercent(label).letter }}
                                                </div>
                                            </div>
                                        </template>
                                    </vue-slider>
                                </div>

                                <!-- Single Dropdown Select -->
                                <div
                                    v-else-if="fields[field.key].type === 'dropdown-code-single'"
                                    class="mb-5 flex space-x-2"
                                >
                                    <CodeSelect
                                        :ref="`codeselect-${field.key}`"
                                        :open="openSubDropdownKey === field.key"
                                        class="w-full"
                                        :id-field="fields[field.key].idField"
                                        :input-type="fields[field.key].type"
                                        :selected-options="fields[field.key].model"
                                        :title="fields[field.key].label"
                                        :code-type="fields[field.key].codeType"
                                        :platform="fields[field.key].platformId || platformId"
                                        :preload="true"
                                        :max-items="5000"
                                        :search-placeholder="fields[field.key].placeholder"
                                        :no-results-text="fields[field.key].noResultsText"
                                        :slider-options="fields[field.key].sliderOptions"
                                        :display-options="
                                            fields[field.key].displayOptions ? fields[field.key].displayOptions() : null
                                        "
                                        :current-filters-context="filtering.current"
                                        :disabled="fields[field.key].disabled ? fields[field.key].disabled() : false"
                                        @update:open="val => openSubDropdownKey = val ? field.key : null"
                                        @selection-change="changeListeners[field.key]"
                                    />
                                </div>

                                <div v-else-if="fields[field.key].type === 'hashtags-input'">
                                    <MultiTagInput
                                        :model-value="fields[field.key].model"
                                        :placeholder="'Type any hashtag you want'"
                                        class="mb-2"
                                        @update:model-value="changeListeners[field.key]"
                                    />
                                </div>
                                <!--:initial-selected-source="source"-->
                                <div v-else-if="fields[field.key].type === 'drop-down-source'">
                                    <DropdownSource
                                        :platform-id="platformId"
                                        :lists-by-platform-id="lists"
                                        :includePlatform="false"
                                        :includeCampaigns="false"
                                        :showNoSourceSelected="true"
                                        noSourceSelectedMessage="No creators excluded"
                                        :counts="{}"
                                        :initialSelectedSource="fields[field.key].model ? {  type : 'list', input : fields[field.key].model[0] } : null"
                                        @change="changeListeners[field.key]"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-show="openFieldKey !== groupKey && group?.simple_fields?.length > 0" class="px-5 pb-3 pt-1">
                    <div v-for="field in group.simple_fields" :key="field.key">
                        <span
                            class="inline-flex cursor-pointer rounded bg-slate-100 py-0.5 pl-2 pr-1.5 text-xs font-medium text-slate-500 hover:bg-slate-200"
                            @click.stop="groupKey !== 'aggregation' ? removeItemFromFilterById(field.key, field.id) : null"
                        >
                            <span
                                ><span class="font-semibold text-slate-600">{{ field.label }}</span>
                                {{ field.text }}</span
                            >
                            <span v-if="field.is_resetable !== false" class="my-auto ml-1">
                                <AppIcon name="xmark" class="h-3 w-3" />
                            </span>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <AdvancedFiltersDialog
        :open="advancedFiltersModal.isOpen"
        :field-label="fields[advancedFiltersModal.fieldKey]?.label"
        :item-label="getAdvancedFiltersModalItemLabel()"
        :specifics="advancedFiltersModalSpecifics"
        :temp-values="advancedFiltersModal.tempValues"
        @update:open="onAdvancedFiltersModalOpenChange"
        @update:temp-values="advancedFiltersModal.tempValues = $event"
        @cancel="closeAdvancedFiltersModal"
        @apply="applyAdvancedFilters"
        @validate-input-key-press="validateInputKeyPress"
    />
</template>

<script>
import { mapGetters } from 'vuex'
import numeral from 'numeral'
import voca from 'voca'
import _ from 'lodash'
import Promise from 'bluebird'
import VueSlider from 'vue-slider-component'
import CodeSelect from '@/components/CodeSelect.vue'
import { getGradeAndColorsFromPercent } from '@/utils/affinity'
import Tooltip from '@/components/Tooltip.vue'
import MultiTagInput from '@/components/inputs/MultiTagInput.vue'

import targetGroupTools from '@/utils/searching/targetgroups'
import DropdownSource from '@/components/dropdowns/DropdownDiscoverSource.vue'
import AdvancedFiltersDialog from '@/partials/options/AdvancedFiltersDialog.vue'

const histogramSteps = {
    'avg_viewers': [
        {
            'value': 0,
            'step': 10
        },
        {
            'value': 100,
            'step': 100
        },
        {
            'value': 1000,
            'step': 1000
        },
        {
            'value': 10000,
            'step': 10000
        }
    ],
    'avg_views': [
        {
            'value': 0,
            'step': 1000
        },
        {
            'value': 10000,
            'step': 5000
        },
        {
            'value': 100000,
            'step': 50000
        },
        {
            'value': 500000,
            'step': 100000
        },
        {
            'value': 1000000,
            'step': 1000000
        }
    ],
    'sum_views': [
        {
            'value': 0,
            'step': 10000
        },
        {
            'value': 100000,
            'step': 50000
        },
        {
            'value': 500000,
            'step': 100000
        },
        {
            'value': 1000000,
            'step': 1000000
        },
        {
            'value': 10000000,
            'step': 10000000
        }
    ],
    'avg_likes': [
        {
            'value': 0,
            'step': 10
        },
        {
            'value': 100,
            'step': 50
        },
        {
            'value': 500,
            'step': 100
        },
        {
            'value': 1000,
            'step': 1000
        },
        {
            'value': 10000,
            'step': 10000
        },
        {
            'value': 100000,
            'step': 100000
        }
    ],
    'avg_comments': [
        {
            'value': 0,
            'step': 10
        },
        {
            'value': 100,
            'step': 50
        },
        {
            'value': 500,
            'step': 100
        },
        {
            'value': 1000,
            'step': 1000
        },
        {
            'value': 10000,
            'step': 10000
        },
        {
            'value': 100000,
            'step': 100000
        }
    ],
    'avg_engagement_rate': [
        {
            'value': 0,
            'step': 0.01
        },
        {
            'value': 0.1,
            'step': 0.05
        },
        {
            'value': 0.25,
            'step': 0.25
        },
        {
            'value': 1,
            'step': 1
        }
    ],
    'audience_cohesion_score': [
        {
            'value': 0,
            'step': 0.05
        },
        {
            'value': 1,
            'step': 1
        }
    ],
    'audience_toxicity_score': [
        {
            'value': 0,
            'step': 0.05
        },
        {
            'value': 1,
            'step': 1
        }
    ],
    'audience_brand_safety': [
        {
            'value': 0,
            'step': 0.05
        },
        {
            'value': 1,
            'step': 1
        }
    ],
    'followers': [
        {
            'value': 0,
            'step': 1000
        },
        {
            'value': 10000,
            'step': 5000
        },
        {
            'value': 50000,
            'step': 10000
        },
        {
            'value': 100000,
            'step': 100000
        },
        {
            'value': 1000000,
            'step': 1000000
        }
    ],
    'global_followers': [
        {
            'value': 0,
            'step': 1000
        },
        {
            'value': 10000,
            'step': 5000
        },
        {
            'value': 50000,
            'step': 10000
        },
        {
            'value': 100000,
            'step': 100000
        },
        {
            'value': 1000000,
            'step': 1000000
        }
    ],
    'sum_content_items': [
        {
            'value': 0,
            'step': 1
        },
        {
            'value': 25,
            'step': 5
        },
        {
            'value': 50,
            'step': 10
        },
        {
            'value': 100,
            'step': 50
        },
        {
            'value': 500,
            'step': 500
        }
    ]
}

const formatters = {
    withMax: (value, { max, displayMax }) => {
        if (value >= max) return `${numeral(displayMax).format('0,0')}`
        return numeral(value).format('0,0')
    },
    default: (value) => numeral(value).format('0,0'),
    thousandsWithMax: (value, { max, displayMax }) => {
        if (value >= max) return `${numeral(displayMax).format('0a')}`
        return numeral(value).format('0a')
    },
    subPercent: (value, { max } = {}) => {
        // You can add logic here if you want to display a '+' for maximum values:
        if (max !== undefined && value >= max && value !== 1) {
            return `${numeral(value * 100).format('0.00')}`
        }
        return numeral(value * 100).format('0.00')
    },
    percent: (value, { max } = {}) => {
        // You can add logic here if you want to display a '+' for maximum values:
        if (max !== undefined && value >= max && value !== 1) {
            return `${numeral(value * 100).format('0')}`
        }
        return numeral(value * 100).format('0')
    },
    percentFromInteger: (value) => numeral(value / 100).format('0%'),
    integer: (value) => numeral(value).format('0,0'),
}

export default {
    name: 'TargetingFilters',

    components: {
        CodeSelect,
        VueSlider,
        Tooltip,
        MultiTagInput,
        DropdownSource,
        AdvancedFiltersDialog,
    },

    props: {
        platformId: {
            type: String,
        },

        filtering: {
            type: Object,
        },
        lists : {
             type: Object,
        }
    },

    emits: ['filters-change', 'filter-change-single', 'filter-change-multiple'],

    data() {
        return {
            formatters,

            getGradeAndColorsFromPercent,

            openFieldKey: null,
            openSubDropdownKey: null,

            // Advanced filters modal state
            advancedFiltersModal: {
                isOpen: false,
                currentItem: null,
                fieldKey: null,
                tempValues: {},
            },

            histogramsByField: {},
            histogramLoadingFields: {}, // Track which histogram fields are currently loading

            groups: {
                aggregation: {
                    label: 'Aggregation',
                    icon: 'calendar',
                    order: 0,
                },
                niche: {
                    label: 'Niche',
                    icon: 'bullseye-pointer',
                    order: 1,
                },
                metrics: {
                    label: 'Metrics',
                    icon: 'chart-line',
                    order: 2,
                },
                channel: {
                    label: 'Characteristics',
                    icon: 'user',
                    order: 3,
                },
                /*
                 * TODO: filters to be revisited at a later date.
                background: {
                    label: 'Background',
                    icon: 'id-card',
                    order: 4,
                },
                */
                content: {
                    label: 'Content',
                    icon: 'list',
                    order: 5,
                },
                /*
                 * TODO: filters to be revisited at a later date.
                business: {
                    label: 'Business',
                    icon: 'briefcase',
                    order: 6,
                },
                */
                audience: {
                    label: 'Audience',
                    icon: 'users-viewfinder',
                    order: 7,
                    condition: () => this.audienceFiltersEnabled,
                },
                games: {
                    label: 'Games',
                    icon: 'gamepad-modern',
                    order: 10,
                    condition: () => ['twitch', 'youtube'].includes(this.platformId)
                },
                /*
                 * TODO: filters to be revisited at a later date.
                restrictions: {
                    label: 'Restrictions',
                    icon: 'flag-pennant',
                    order: 15,
                },
                */
            },

            fields: {
                day_range: {
                    label: 'Timeframe',
                    type: 'radio-list',
                    key: 'day_range',
                    model: null,
                    options: [],
                    tooltip: {
                        title: 'Timeframe',
                        content: [
                            {
                                text: 'Choose how far back Streamforge looks when calculating creator metrics.',
                            },
                        ],
                    },
                    group: 'aggregation',
                },

                content_type: {
                    label: 'Format',
                    type: 'radio-list',
                    key: 'content_type',
                    model: null,
                    options: targetGroupTools.createDiscoveryFilters(this.platformId).options.content_type,
                    tooltip: {
                        title: 'Format',
                        content: [
                            {
                                text: 'Choose which content format to use when to display the performance data in the results.',
                            },
                        ],
                    },
                    group: 'aggregation',
                },

                'classifications.hierarchy.topic': {
                    idField: 'code',
                    label: 'Niche',
                    type: 'dropdown-code-single',
                    key: 'classifications.hierarchy.topic',
                    codeType: 'classifications_topic',
                    placeholder: 'Search niches',
                    tooltip: {
                        title: 'Niche',
                        content: [
                            {
                                text: 'Find creators who regularly post about a specific topic.',
                            },
                        ],
                    },
                    displayOptions: () => ({
                        sort: { field: 'label', direction: 'asc' },
                    }),
                    model: null,
                    group: 'niche',
                },



                /*
                 * TODO: Reinstate when Industry Category is improved.
                'classifications.hierarchy.category': {
                    idField: 'code',
                    label: 'Niche category',
                    type: 'dropdown-code-single',
                    key: 'classifications.hierarchy.category',
                    codeType: 'classifications_category',
                    placeholder: 'Search niche categories',
                    disabled: () => this.filtering.current.classifications.hierarchy.topic === null,
                    tooltip: {
                        title: 'Niche category',
                        content: [
                            {
                                text: 'Narrow the selected niche to a more specific category.',
                            },
                        ],
                    },
                    displayOptions: () => ({
                        sort: { field: 'label', direction: 'asc' },
                    }),
                    model: null,
                    group: 'niche',
                },
                */

                // Checkbox to require the matched niche/category to be the creator's primary classification
                'classifications.primary_only': {
                    label: 'Main niche only',
                    type: 'checkbox',
                    key: 'classifications.primary_only',
                    sub_key: 'primary_only',
                    model: null,
                    tooltip: {
                        title: 'Main niche only',
                        content: [
                            {
                                text: 'Only show creators whose main content focus matches the selected niche (topic).',
                            },
                        ],
                    },
                    // Only show when a niche is selected
                    conditions: () => !!this.filtering?.current?.classifications?.hierarchy?.topic,
                    group: 'niche',
                },

                'games_list.include.values': {
                    label: 'Games',
                    type: 'select-code-multiple',
                    key: 'games_list.include.values',
                    model: null,
                    codeType: 'games_list',
                    placeholder: 'Search for a game',
                    multiplePlaceholder: 'Type to add more games',
                    noResultsText: 'All matches selected',
                    conjunction: {
                        key: 'games_list.include.conjunction',
                        options: ['$and', '$or'],
                    },

                    specifics: [{
                        key: 'content_count',
                        label: 'Content count',
                        type: 'number',
                        inputMin: 1,
                        format : {
                            isPercentage: false,
                            decimals : 0,
                        },
                        model: {
                            min: null,
                            max: null,
                        },
                    },  {
                        key: 'count_percent',
                        label: 'Percent of creator\'s content',
                        type: 'number',
                        format : {
                            isPercentage: true,
                            decimals : 0,
                        },
                        model: {
                            min: null,
                            max: null,
                        },
                    }, {
                        key: 'time_watched_percent',
                        label: 'Percent of creator\'s watch time',
                        type: 'number',
                        format : {
                            isPercentage: true,
                            decimals : 0,
                        },
                        model: {
                            min: null,
                            max: null,
                        },
                        disabled: () => this.platformId !== 'twitch',
                    }, {
                        key: 'time_streamed_percent',
                        label: 'Percent of creator\'s stream time',
                        type: 'number',
                        format : {
                            isPercentage: true,
                            decimals : 0,
                        },
                        model: {
                            min: null,
                            max: null,
                        },
                        disabled: () => this.platformId !== 'twitch',
                    }, {
                        key: 'avg_viewers',
                        label: 'AVG Viewers',
                        type: 'number',
                        format : {
                            isPercentage: false,
                            decimals : 0,
                        },
                        model: {
                            min: null,
                            max: null,
                        },
                        disabled: () => this.platformId !== 'twitch',
                    }, {
                        key: 'avg_views',
                        label: 'Avg. views',
                        type: 'number',
                        format : {
                            isPercentage: false,
                            decimals : 0,
                        },
                        model: {
                            min: null,
                            max: null,
                        },
                        disabled: () => this.platformId !== 'youtube',
                    }, {
                        key: 'sum_views',
                        label: 'Total Views',
                        type: 'number',
                        format : {
                            isPercentage: false,
                            decimals : 0,
                        },
                        model: {
                            min: null,
                            max: null,
                        },
                        disabled: () => this.platformId !== 'youtube',
                    }, {
                        key: 'time_streamed_hours',
                        label: 'Hours streamed',
                        type: 'number',
                        inputMin: 1,
                        format : {
                            isPercentage: false,
                            decimals : 0,
                        },
                        model: {
                            min: null,
                            max: null,
                        },
                        disabled: () => this.platformId !== 'twitch',
                    }, {
                        key: 'has_sponsorship',
                        label: 'Creator has sponsored content',
                        type: 'boolean',
                        model: false,
                    }],

                    // Only show this field if at least 1 game has been selected
                    conditions: () => ['twitch', 'youtube'].includes(this.platformId),

                    tooltip: {
                        title: 'Games',
                        content: [
                            {
                                text: 'Find creators who have made content about specific games.',
                            },
                            {
                                text: 'Games are sourced from the IGDB database.',
                            },
                        ],
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    group: 'games',
                },

                'games_list.exclude.values': {
                    label: 'Excluded games',
                    type: 'select-code-multiple',
                    key: 'games_list.exclude.values',
                    model: null,
                    codeType: 'games_list',
                    placeholder: 'Search for a game',
                    multiplePlaceholder: 'Type to add more games',
                    noResultsText: 'All matches selected',
                    conjunction: {
                        key: 'games_list.exclude.conjunction',
                        options: ['$nin', '$notall'],
                    },
                    tooltip: {
                        title: 'Excluded games',
                        content: [
                            {
                                text: 'Exclude creators who have made content about specific games.',
                            },
                        ],
                    },
                    conditions: () => ['twitch', 'youtube'].includes(this.platformId),
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    group: 'games',
                },

                'games_list_matched_count.min': {
                    label: 'Minimum matching games',
                    type: 'slider-single',
                    key: 'games_list_matched_count.min',
                    model: null,
                    numberType: 'integer',
                    tooltip: {
                        title: 'Minimum matching games',
                        content: [
                            {
                                text: 'Choose how many selected games a creator must have covered.',
                            },
                        ],
                    },

                    slider: {
                        min: 1,
                        max: 10,
                    },

                    conditions: () => {
                        const gamesSelected = _.get(this.filtering.current, 'games_list.include.values', [])
                        const gamesConjunction = _.get(
                            this.filtering.current,
                            'games_list.include.conjunction',
                        )

                        if (_.size(gamesSelected) > 1 && gamesConjunction === '$or') {
                            return true
                        }

                        // If the filter is not visible, reset it so it doesn't affect the results
                        this.resetFilters(['games_list_matched_count.min'])
                        return false
                    },
                    group: 'games',
                },

                'games_count.min': {
                    label: 'Minimum games covered',
                    type: 'slider-single',
                    key: 'games_count.min',
                    model: null,
                    numberType: 'integer',

                    slider: {
                        min: 1,
                        max: 15,
                    },

                    tooltip: {
                        title: 'Minimum games covered',
                        content: [
                            {
                                text: 'Choose the minimum number of different games a creator must have covered within the selected timeframe.',
                            },
                        ],
                    },
                    group: 'games',
                },

                'game_categories_list.include.values': {
                    label: 'Game tags',
                    type: 'select-code-multiple',
                    key: 'game_categories_list.include.values',
                    model: null,
                    codeType: 'game_categories_list',
                    placeholder: 'Search for a game tag',
                    multiplePlaceholder: 'Type to add more game tags',
                    noResultsText: 'All matches selected',
                    tooltip: {
                        title: 'Game tags',
                        content: [
                            {
                                text: 'Find creators based on the types of games they cover using Steam game tags.',
                            },
                        ],
                    },
                    conjunction: {
                        key: 'game_categories_list.include.conjunction',
                        options: ['$and', '$or'],
                    },

                     specifics: [{
                        key: 'content_count',
                        label: 'Content count',
                        type: 'number',
                        format : {
                            isPercentage: false,
                            decimals : 0,
                        },
                        model: {
                            min: null,
                            max: null,
                        },
                    },
                    {
                        key: 'count_percent',
                        label: 'Percent of creator\'s content',
                        type: 'number',
                        format : {
                            isPercentage: true,
                            decimals : 0,
                        },
                        model: {
                            min: null,
                            max: null,
                        },
                    }, {
                        key: 'time_watched_percent',
                        label: 'Percent of creator\'s watch time',
                        type: 'number',
                        format : {
                            isPercentage: true,
                            decimals : 0,
                        },
                        model: {
                            min: null,
                            max: null,
                        },
                        disabled: () => this.platformId !== 'twitch',
                    }, {
                        key: 'time_streamed_percent',
                        label: 'Percent of creator\'s stream time',
                        type: 'number',
                        format : {
                            isPercentage: true,
                            decimals : 0,
                        },
                        model: {
                            min: null,
                            max: null,
                        },
                        disabled: () => this.platformId !== 'twitch',
                    }, {
                        key: 'avg_viewers',
                        label: 'AVG Viewers',
                        type: 'number',
                        format : {
                            isPercentage: false,
                            decimals : 0,
                        },
                        model: {
                            min: null,
                            max: null,
                        },
                        disabled: () => this.platformId !== 'twitch',
                    }, {
                        key: 'avg_views',
                        label: 'Avg. views',
                        type: 'number',
                        format : {
                            isPercentage: false,
                            decimals : 0,
                        },
                        model: {
                            min: null,
                            max: null,
                        },
                        disabled: () => this.platformId !== 'youtube',
                    }, {
                        key: 'sum_views',
                        label: 'Total Views',
                        type: 'number',
                        format : {
                            isPercentage: false,
                            decimals : 0,
                        },
                        model: {
                            min: null,
                            max: null,
                        },
                        disabled: () => this.platformId !== 'youtube',
                    }, {
                        key: 'sum_views_percent',
                        label: 'Percent of creator\'s total views',
                        type: 'number',
                        format : {
                            isPercentage: true,
                            decimals : 0,
                        },
                        model: {
                            min: null,
                            max: null,
                        },
                        disabled: () => this.platformId !== 'youtube',
                    }, {
                        key: 'time_streamed_percent',
                        label: 'Percent of creator\'s stream time',
                        type: 'number',
                        format : {
                            isPercentage: true,
                            decimals : 0,
                        },
                        model: {
                            min: null,
                            max: null,
                        },
                        disabled: () => this.platformId !== 'twitch',
                    }, {
                        key: 'has_sponsorship',
                        label: 'Creator has sponsored content',
                        type: 'boolean',
                        model: false,
                    }],
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    group: 'games',
                },

                'game_categories_list.exclude.values': {
                    label: 'Excluded game tag',
                    type: 'select-code-multiple',
                    key: 'game_categories_list.exclude.values',
                    model: null,
                    codeType: 'game_categories_list',
                    placeholder: 'Search for a game tag',
                    multiplePlaceholder: 'Type to add more game tags',
                    noResultsText: 'All matches selected',
                    tooltip: {
                        title: 'Excluded game tag',
                        content: [
                            {
                                text: 'Exclude creators based on the types of games they cover using Steam game tags.',
                            },
                        ],
                    },
                    conjunction: {
                        key: 'game_categories_list.exclude.conjunction',
                        options: ['$nin', '$notall'],
                    },

                     specifics: [{
                        key: 'content_count',
                        label: 'Content count',
                        type: 'number',
                        format : {
                            isPercentage: false,
                            decimals : 0,
                        },
                        model: {
                            min: null,
                            max: null,
                        },
                    },
                    {
                        key: 'count_percent',
                        label: 'Percent of creator\'s content',
                        type: 'number',
                        format : {
                            isPercentage: true,
                            decimals : 0,
                        },
                        model: {
                            min: null,
                            max: null,
                        },
                    }, {
                        key: 'time_watched_percent',
                        label: 'Percent of creator\'s watch time',
                        type: 'number',
                        format : {
                            isPercentage: true,
                            decimals : 0,
                        },
                        model: {
                            min: null,
                            max: null,
                        },
                        disabled: () => this.platformId !== 'twitch',
                    }, {
                        key: 'time_streamed_percent',
                        label: 'Percent of creator\'s stream time',
                        type: 'number',
                        format : {
                            isPercentage: true,
                            decimals : 0,
                        },
                        model: {
                            min: null,
                            max: null,
                        },
                        disabled: () => this.platformId !== 'twitch',
                    }, {
                        key: 'avg_viewers',
                        label: 'AVG Viewers',
                        type: 'number',
                        format : {
                            isPercentage: false,
                            decimals : 0,
                        },
                        model: {
                            min: null,
                            max: null,
                        },
                        disabled: () => this.platformId !== 'twitch',
                    }, {
                        key: 'avg_views',
                        label: 'Avg. views',
                        type: 'number',
                        format : {
                            isPercentage: false,
                            decimals : 0,
                        },
                        model: {
                            min: null,
                            max: null,
                        },
                        disabled: () => this.platformId !== 'youtube',
                    }, {
                        key: 'sum_views',
                        label: 'Total Views',
                        type: 'number',
                        format : {
                            isPercentage: false,
                            decimals : 0,
                        },
                        model: {
                            min: null,
                            max: null,
                        },
                        disabled: () => this.platformId !== 'youtube',
                    }, {
                        key: 'sum_views_percent',
                        label: 'Percent of creator\'s total views',
                        type: 'number',
                        format : {
                            isPercentage: true,
                            decimals : 0,
                        },
                        model: {
                            min: null,
                            max: null,
                        },
                        disabled: () => this.platformId !== 'youtube',
                    }, {
                        key: 'has_sponsorship',
                        label: 'Creator has sponsored content',
                        type: 'boolean',
                        model: false,
                    }],
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    group: 'games',
                },

                /*

                'interests.include.values': {
                    label: 'Interests',
                    type: 'select-code-multiple',
                    key: 'interests.include.values',
                    model: null,
                    codeType: 'interests',
                    placeholder: 'Search for an interest',
                    multiplePlaceholder: 'Type to add more interests',
                    noResultsText: 'All matches selected',
                    conjunction: {
                        key: 'interests.include.conjunction',
                        options: ['$and', '$or'],
                    },
                    tooltip: {
                        title: 'Interests',
                        content: [
                            {
                                text: 'Find creators who regularly talk about specific topics based on AI analysis of their content across platforms and public online presence.',
                            },
                        ],
                    },

                    specifics: [{
                        key: 'affinity',
                        label: 'Affinity to Interest',
                        type: 'number',
                        format : {
                            isPercentage: false,
                            decimals : 1,
                        },
                        model: {
                            min: null,
                            max: null,
                        },
                    }],
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    group: 'niche',
                },

                'interests.exclude.values': {
                    label: 'Excluded interests',
                    type: 'select-code-multiple',
                    key: 'interests.exclude.values',
                    model: null,
                    codeType: 'interests',
                    placeholder: 'Search for an interest',
                    multiplePlaceholder: 'Type to add more interests',
                    noResultsText: 'All matches selected',
                    conjunction: {
                        key: 'interests.exclude.conjunction',
                        options: ['$and', '$or'],
                    },
                    tooltip: {
                        title: 'Excluded interests',
                        content: [
                            {
                                text: 'Exclude creators who regularly talk about specific topics based on AI analysis of their content across platforms and public online presence.',
                            },
                        ],
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    group: 'niche',
                },

                */
                'semantic_interests.include.values': {
                    label: 'Interests',
                    type: 'select-code-multiple',
                    key: 'semantic_interests.include.values',
                    model: null,
                    codeType: 'semantic_interests',
                    placeholder: 'Search for an interest',
                    multiplePlaceholder: 'Type to add more interests',
                    noResultsText: 'All matches selected',
                    conjunction: {
                        key: 'semantic_interests.include.conjunction',
                        options: ['$and', '$or'],
                    },
                    tooltip: {
                        title: 'Interests',
                        content: [
                            {
                                text: 'Find creators who regularly talk about specific topics based on AI analysis of their content across platforms and public online presence.',
                            },
                        ],
                    },

                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    group: 'niche',
                },

                'semantic_interests.exclude.values': {
                    label: 'Excluded interests',
                    type: 'select-code-multiple',
                    key: 'semantic_interests.exclude.values',
                    model: null,
                    codeType: 'semantic_interests',
                    placeholder: 'Search for an interest',
                    multiplePlaceholder: 'Type to add more interests',
                    noResultsText: 'All matches selected',
                    conjunction: {
                        key: 'semantic_interests.exclude.conjunction',
                        options: ['$and', '$or'],
                    },
                    tooltip: {
                        title: 'Excluded interests',
                        content: [
                            {
                                text: 'Exclude creators who regularly talk about specific topics based on AI analysis of their content across platforms and public online presence.',
                            },
                        ],
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    group: 'niche',
                },

                'hashtags_list.include.values': {
                    label: 'Hashtag',
                    type: 'hashtags-input',
                    key: 'hashtags_list.include.values',
                    model: null,
                    tooltip: {
                        title: 'Hashtag',
                        content: [
                            {
                                text: 'Search for creators who have posted content using specific hashtags.',
                            },
                        ],
                    },
                    conjunction: {
                        key: 'hashtags_list.include.conjunction',
                        options: ['$and', '$or'],
                    },
                    specifics: [{
                        key: 'content_count',
                        label: 'Content count',
                        type: 'number',
                        format : {
                            isPercentage: false,
                            decimals : 0,
                        },
                        model: {
                            min: null,
                            max: null,
                        },
                    }],
                    group: 'content',
                },

                'hashtags_list.exclude.values': {
                    label: 'Excluded hashtag',
                    type: 'hashtags-input',
                    key: 'hashtags_list.exclude.values',
                    model: null,
                    tooltip: {
                        title: 'Excluded hashtag',
                        content: [
                            {
                                text: 'Exclude creators who have posted content using specific hashtags.',
                            },
                        ],
                    },
                    conjunction: {
                        key: 'hashtags_list.exclude.conjunction',
                        options: ['$and', '$or'],
                    },
                    specifics: [{
                        key: 'content_count',
                        label: 'Content count',
                        type: 'number',
                        format : {
                            isPercentage: false,
                            decimals : 0,
                        },
                        model: {
                            min: null,
                            max: null,
                        },
                    }],
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    group: 'content',
                },

                'content_formats.include.values': {
                    label: 'Content style',
                    type: 'select-code-multiple',
                    key: 'content_formats.include.values',
                    model: null,
                    codeType: 'content_formats',
                    placeholder: 'Search for a content style',
                    multiplePlaceholder: 'Type to add more content styles',
                    noResultsText: 'All matches selected',
                    tooltip: {
                        title: 'Content style',
                        content: [
                            {
                                text: 'Find creators based on AI analysis of the types of content they regularly create, like vlogs, reactions, tutorials, or sketches.',
                            },
                        ],
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    group: 'content',
                },

                'content_formats.exclude.values': {
                    label: 'Excluded content style',
                    type: 'select-code-multiple',
                    key: 'content_formats.exclude.values',
                    model: null,
                    codeType: 'content_formats',
                    placeholder: 'Search for a content style',
                    multiplePlaceholder: 'Type to add more content styles',
                    noResultsText: 'All matches selected',
                    tooltip: {
                        title: 'Excluded content style',
                        content: [
                            {
                                text: 'Exclude creators based on AI analysis of the types of content they regularly create, like vlogs, reactions, tutorials, or sketches.',
                            },
                        ],
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    group: 'content',
                },

                account_rating_remapped: {
                    label: 'Content rating',
                    type: 'select-code-multiple',
                    key: 'account_rating_remapped',
                    model: null,
                    codeType: 'account_rating_remapped',
                    placeholder: 'Search for a content rating',
                    multiplePlaceholder: 'Type to add more content ratings',
                    noResultsText: 'All matches selected',
                    tooltip: {
                        title: 'Content rating',
                        content: [
                            {
                                text: 'Filter creators based on AI analysis of the language, themes, and overall maturity of their content.',
                            },
                        ],
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    group: 'content',
                },

                'has_sponsorship': {
                    label: 'Has sponsored content',
                    type: 'checkbox',
                    key: 'has_sponsorship',
                    sub_key: 'has_sponsorship',
                    model: null,
                    tooltip: {
                        title: 'Has sponsored content',
                        content: [
                            { text: 'Find creators who have shared sponsored content identified through sponsorship-related hashtags.' },
                        ],
                    },
                    group: 'content',
                },

                avg_viewers: {
                    label: 'AVG Concurrent Viewers',
                    type: 'slider-range',
                    key: 'avg_viewers',
                    dragValues: null,
                    model: null,
                    points: histogramSteps.avg_viewers,
                    maxDisplayValue: 10_000,
                    tooltipFormatter: formatters.withMax,
                    inputFormatter: formatters.withMax,
                    conditions: () => ['stream'].includes(this.filtering?.current?.content_type),
                    tooltip: {
                        title: 'Average Concurrent Viewers (ACV)',
                        content: [
                            {
                                text: 'This is on average how many viewers are actively watching a live stream at once. How many people are watching a live stream is checked on a regular interval and averaged together afterwards. We then aggregate this concurrent viewership data together to arrive at a total average for the creator.',
                            },
                            {
                                text: 'Also known as ACV, this metric is the primary KPI for influencer marketing campaigns on live streaming platforms.',
                            },
                        ],
                    },
                    group: 'metrics',
                },

                avg_views: {
                    label: 'Avg. views',
                    type: 'slider-range',
                    key: 'avg_views',
                    dragValues: null,
                    model: null,
                    points: histogramSteps.avg_views,
                    maxDisplayValue: 1_000_000,
                    minInputLabel: '1',
                    maxInputLabel: 'No max',
                    tooltipFormatter: formatters.thousandsWithMax,
                    inputFormatter: formatters.withMax,
                    tooltip: {
                        title: 'Average views',
                        content: [
                            {
                                text: 'Average views across a creator\'s recent content based on the selected format and timeframe.',
                            },
                        ],
                    },
                    conditions: () => ['youtube', 'tiktok', 'twitter', 'instagram'].includes(this.platformId),
                    group: 'metrics',
                },

                avg_likes: {
                    label: 'Avg. likes',
                    type: 'slider-range',
                    key: 'avg_likes',
                    dragValues: null,
                    model: null,
                    points: histogramSteps.avg_likes,
                    maxDisplayValue: 1_000_000,
                    minInputLabel: '1',
                    maxInputLabel: 'No max',
                    tooltipFormatter: formatters.thousandsWithMax,
                    inputFormatter: formatters.withMax,
                    tooltip: {
                        title: 'Average likes',
                        content: [
                            {
                                text: 'Average likes across a creator\'s recent content based on the selected format and timeframe.',
                            },
                        ],
                    },
                    conditions: () => ['youtube', 'tiktok', 'twitter', 'instagram'].includes(this.platformId),
                    group: 'metrics',
                },

                sum_content_items: {
                    label: 'Minimum posts',
                    type: 'slider-range',
                    key: 'sum_content_items',
                    dragValues: null,
                    model: null,
                    points: histogramSteps.sum_content_items,
                    maxDisplayValue: 500,
                    minInputLabel: '1',
                    maxInputLabel: 'No Max',
                    tooltipFormatter: formatters.thousandsWithMax,
                    inputFormatter: formatters.withMax,
                    tooltip: {
                        title: 'Minimum post',
                        content: [
                            {
                                text: 'Choose how many times a creator must have posted within the selected timeframe.',
                            },
                        ],
                    },
                    group: 'metrics',
                },

                avg_engagement_rate: {
                    label: 'Engagement rate',
                    type: 'slider-range',
                    key: 'avg_engagement_rate',
                    numberType: 'percent', // indicate this field uses percent formatting
                    dragValues: null,
                    model: null,
                    points: histogramSteps.avg_engagement_rate,
                    maxDisplayValue: 1,
                    maxInputLabel: 'No max',
                    tooltipFormatter: formatters.percent,
                    inputFormatter: formatters.percent,
                    tooltip: {
                        title: 'Engagement rate',
                        content: [
                            {
                                text: 'Average engagement rate across a creator’s recent content based on the selected format and timeframe.',
                            },
                        ],
                    },
                    group: 'metrics',
                },

                followers: {
                    label: 'Followers',
                    type: 'slider-range',
                    key: 'followers',
                    dragValues: null,
                    model: null,
                    points: histogramSteps.followers,
                    maxDisplayValue: 500_000_000,
                    minInputLabel: '1',
                    maxInputLabel: 'No max',
                    tooltipFormatter: formatters.thousandsWithMax,
                    inputFormatter: formatters.withMax,
                    tooltip: {
                        title: 'Total follower count',
                        content: [
                            {
                                text: 'Current follower count on the selected platform.',
                            },
                        ],
                    },
                    group: 'metrics',
                },

                'account_type': {
                    label: 'Account type',
                    type: 'select-code-multiple',
                    key: 'account_type',
                    model: null,
                    codeType: 'account_type',
                    placeholder: 'Search for an account type',
                    multiplePlaceholder: 'Type to add more account types',
                    noResultsText: 'All matches selected',
                    tooltip: {
                        title: 'Account type',
                        content: [
                            {
                                text: 'Find accounts based on AI analysis of whether they represent an individual, brand, group, or fan community.',
                            },
                        ],
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    group: 'channel',
                },

                'alternate_platforms.include.values': {
                    label: 'Other Socials',
                    type: 'select-code-multiple',
                    key: 'alternate_platforms.include.values',
                    model: null,
                    codeType: 'alternate_platforms',
                    placeholder: 'Search for a platform',
                    multiplePlaceholder: 'Type to add more platforms',
                    noResultsText: 'All matches selected',
                    conjunction: {
                        key: 'alternate_platforms.include.conjunction',
                        options: ['$and', '$or']
                    },
                    specifics: [{
                        key: 'followers',
                        label: 'Followers',
                        type: 'number',
                        format : {
                            isPercentage: false,
                            decimals : 0,
                        },
                        model: {
                            min: null,
                            max: null,
                        },
                    }],
                    tooltip: {
                        title: 'Alternate Platforms',
                        content: [
                            {
                                text: 'This will require a creator to have a public facing profile on this other platform.',
                            },
                        ],
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    group: 'channel',
                },

                'email': {
                    label: 'Has email address',
                    type: 'checkbox',
                    key: 'email',
                    sub_key: 'email',
                    model: null,
                    tooltip: {
                        title: 'Has email address',
                        content: [
                            {
                                text: 'Only show creators with a publicly available email address on their platforms or online presence.',
                            },
                            {
                                text: 'Streamforge does not collect email addresses hidden behind captchas or protected forms.',
                            },
                        ],
                    },
                    group: 'channel',
                },

                language: {
                    label: 'Language',
                    type: 'select-code-multiple',
                    key: 'language',
                    model: null,
                    codeType: 'language',
                    placeholder: 'Search for a language',
                    multiplePlaceholder: 'Type to add more languages',
                    noResultsText: 'All matches selected',
                    tooltip: {
                        title: 'Content language',
                        content: [
                            {
                                text: 'Find creators who regularly create content in specific languages based on publicly available information and AI analysis of their content and online presence.',
                            },
                        ],
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    group: 'channel',
                },

                exclude_language: {
                    label: 'Excluded language',
                    type: 'select-code-multiple',
                    key: 'exclude_language',
                    model: null,
                    codeType: 'language',
                    placeholder: 'Search for a language',
                    multiplePlaceholder: 'Type to add more languages',
                    noResultsText: 'All matches selected',
                    tooltip: {
                        title: 'Excluded language',
                        content: [
                            {
                                text: 'Exclude creators who regularly create content in specific languages based on publicly available information and AI analysis of their content and online presence.',
                            },
                        ],
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    group: 'channel',
                },

               country: {
                    label: 'Country',
                    type: 'select-code-multiple',
                    key: 'country',
                    model: null,
                    codeType: 'country',
                    placeholder: 'Search for a country',
                    multiplePlaceholder: 'Type to add more countries',
                    noResultsText: 'All matches selected',
                    tooltip: {
                        title: 'Country of residence',
                        content: [
                            {
                                text: 'Find creators located in specific countries based on publicly available information and AI analysis of their content and online presence.',
                            },
                        ],
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    group: 'channel',
                },

                exclude_country: {
                    label: 'Excluded country',
                    type: 'select-code-multiple',
                    key: 'exclude_country',
                    model: null,
                    codeType: 'country',
                    placeholder: 'Search for a country',
                    multiplePlaceholder: 'Type to add more countries',
                    noResultsText: 'All matches selected',
                    tooltip: {
                        title: 'Excluded countries',
                        content: [
                            {
                                text: 'Exclude creators located in specific countries based on publicly available information and AI analysis of their content and online presence.',
                            },
                        ],
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    group: 'channel',
                },
                region: {
                    label: 'Region',
                    type: 'select-code-multiple',
                    key: 'region',
                    model: null,
                    codeType: 'region',
                    placeholder: 'Search for a region',
                    multiplePlaceholder: 'Type to add more regions',
                    noResultsText: 'All matches selected',
                    tooltip: {
                        title: 'Region of residence',
                        content: [
                            {
                                text: 'Find creators located in specific regions based on publicly available information and AI analysis of their content and online presence.',
                            },
                        ],
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    group: 'channel',
                },

                // City works like Country
                residence_city: {
                    label: 'City',
                    type: 'select-code-multiple',
                    key: 'residence_city',
                    model: null,
                    codeType: 'residence_city',
                    placeholder: 'Search for a city',
                    multiplePlaceholder: 'Type to add more cities',
                    noResultsText: 'All matches selected',
                    tooltip: {
                        title: 'City of residence',
                        content: [
                            {
                                text: 'Find creators based on the city they publicly share across their content and online presence.',
                            },
                        ],
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    group: 'channel',
                },

                gender: {
                    label: 'Gender',
                    type: 'select-code-multiple',
                    key: 'gender',
                    model: null,
                    codeType: 'gender',
                    placeholder: 'Search for a gender',
                    multiplePlaceholder: 'Type to add more genders',
                    noResultsText: 'All matches selected',
                    tooltip: {
                        title: 'Gender',
                        content: [
                            {
                                text: 'Find creators based on AI analysis of how they publicly present themselves across their content and online presence.',
                            },
                        ],
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    group: 'channel',
                },

                age_range: {
                    label: 'Age range',
                    type: 'select-code-multiple',
                    key: 'age_range',
                    model: null,
                    codeType: 'age_range',
                    placeholder: 'Search for an age range',
                    multiplePlaceholder: 'Type to add more age ranges',
                    noResultsText: 'All matches selected',
                    tooltip: {
                        title: 'Age range',
                        content: [
                            {
                                text: 'Find creators based on AI analysis of the age range they appear to fall into across publicly available information and AI analysis of their content and online presence.',
                            },
                        ],
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'channels',
                        sort: {
                            field: 'label',
                            direction: 'asc',
                        }
                    }),
                    group: 'channel',
                },

                // === Characteristics: Family ===
                'has_relationship': {
                    label: 'In a relationship',
                    type: 'checkbox',
                    key: 'has_relationship',
                    sub_key: 'has_relationship',
                    model: null,
                    tooltip: {
                        title: 'Relationship status',
                        content: [
                            { text: 'Find creators that are in a relationship based on AI analysis of the information they publicly share across their content and online presence.' },
                        ],
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: { field: 'label', direction: 'asc' },
                    }),
                    group: 'channel',
                },

                'has_children': {
                    label: 'Parent creator',
                    type: 'checkbox',
                    key: 'has_children',
                    sub_key: 'has_children',
                    model: null,
                    tooltip: {
                        title: 'Parent creator',
                        content: [
                            { text: 'Find creators who publicly share parenting or family life as part of their content and online presence.' },
                        ],
                    },
                    group: 'channel',
                },

                // === Characteristics: Appearance ===
                'shows_face': {
                    label: 'Shows face',
                    type: 'checkbox',
                    key: 'shows_face',
                    sub_key: 'shows_face',
                    model: null,
                    tooltip: {
                        title: 'Shows face',
                        content: [
                            { text: 'Only show creators who regularly appear on camera in their content.' },
                        ],
                    },
                    group: 'channel',
                },

                'is_vtuber': {
                    label: 'Vtubers only',
                    type: 'checkbox',
                    key: 'is_vtuber',
                    sub_key: 'is_vtuber',
                    model: null,
                    tooltip: {
                        title: 'VTubers',
                        content: [
                            { text: 'Only show creators who primarily appear as a virtual character or avatar in their content.' },
                        ],
                    },
                    group: 'channel',
                },
                'is_not_vtuber': {
                    label: 'Exclude VTubers',
                    type: 'checkbox',
                    key: 'is_not_vtuber',
                    sub_key: 'is_not_vtuber',
                    model: null,
                    tooltip: {
                        title: 'Exclude VTubers',
                        content: [
                            { text: 'Exclude creators who primarily appear as a virtual character or avatar in their content.' },
                        ],
                    },
                    group: 'channel',
                },
                // === Characteristics: Creator Type ===
                'is_ai_generated_influencer': {
                    label: 'AI accounts',
                    type: 'checkbox',
                    key: 'is_ai_generated_influencer',
                    sub_key: 'is_ai_generated_influencer',
                    model: null,
                    tooltip: {
                        title: 'AI accounts',
                        content: [
                            { text: 'Only show accounts primarily represented through AI-generated content or identities.' },
                        ],
                    },
                    group: 'channel',
                },

                'is_not_ai_generated_influencer': {
                    label: 'Exclude AI accounts',
                    type: 'checkbox',
                    key: 'is_not_ai_generated_influencer',
                    sub_key: 'is_not_ai_generated_influencer',
                    model: null,
                    tooltip: {
                        title: 'Exclude AI accounts',
                        content: [
                            { text: 'Exclude accounts primarily represented through AI-generated content or identities.' },
                        ],
                    },
                    group: 'channel',
                },


                /*
                 * TODO: filters to be revisited at a later date.
                // === Education ===
                'education_institution.include.values': {
                    label: 'Education — Institutions',
                    type: 'select-code-multiple',
                    key: 'education_institution.include.values',
                    model: null,
                    codeType: 'education_institution',
                    placeholder: 'Search institution',
                    multiplePlaceholder: 'Type to add institutions',
                    noResultsText: 'All matches selected',
                    conjunction: {
                        key: 'education_institution.include.conjunction',
                        options: ['$and', '$or'],
                    },
                    tooltip: {
                        title: 'Institutions',
                        content: [
                            { text: 'Universities and colleges listed in the creator’s background.' },
                        ],
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    group: 'background',
                },
                'education_field.include.values': {
                    label: 'Education — Fields of Study',
                    type: 'select-code-multiple',
                    key: 'education_field.include.values',
                    model: null,
                    codeType: 'education_field',
                    placeholder: 'Search field',
                    multiplePlaceholder: 'Type to add fields',
                    noResultsText: 'All matches selected',
                    conjunction: {
                        key: 'education_field.include.conjunction',
                        options: ['$and', '$or'],
                    },
                    tooltip: {
                        title: 'Fields of Study',
                        content: [
                            { text: 'Majors and academic fields from the creator’s education.' },
                        ],
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    group: 'background',
                },
                */
                /*
                 * TODO: filters to be revisited at a later date.
                // === Career ===
                'career_role.include.values': {
                    label: 'Career — Roles',
                    type: 'select-code-multiple',
                    key: 'career_role.include.values',
                    model: null,
                    codeType: 'career_role',
                    placeholder: 'Search role',
                    multiplePlaceholder: 'Type to add roles',
                    noResultsText: 'All matches selected',
                    conjunction: {
                        key: 'career_role.include.conjunction',
                        options: ['$and', '$or'],
                    },
                    tooltip: {
                        title: 'Career Roles',
                        content: [
                            { text: 'Job titles from the creator’s career history. Use “Current job only” to restrict.' },
                        ],
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    group: 'background',
                },
                'career_industry.include.values': {
                    label: 'Career — Industries',
                    type: 'select-code-multiple',
                    key: 'career_industry.include.values',
                    model: null,
                    codeType: 'career_industry',
                    placeholder: 'Search industry',
                    multiplePlaceholder: 'Type to add industries',
                    noResultsText: 'All matches selected',
                    conjunction: {
                        key: 'career_industry.include.conjunction',
                        options: ['$and', '$or'],
                    },
                    tooltip: {
                        title: 'Career Industries',
                        content: [
                            { text: 'Industries worked in (e.g., Media, Technology). Use “Current job only” to restrict.' },
                        ],
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    group: 'background',
                },
                'career_company.include.values': {
                    label: 'Career — Companies',
                    type: 'select-code-multiple',
                    key: 'career_company.include.values',
                    model: null,
                    codeType: 'career_company',
                    placeholder: 'Search company',
                    multiplePlaceholder: 'Type to add companies',
                    noResultsText: 'All matches selected',
                    conjunction: {
                        key: 'career_company.include.conjunction',
                        options: ['$and', '$or'],
                    },
                    tooltip: {
                        title: 'Career Companies',
                        content: [
                            { text: 'Employers from the creator’s career history. Use “Current job only” to restrict.' },
                        ],
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    group: 'background',
                },
                'career_current_only': {
                    label: 'Current job only',
                    type: 'checkbox',
                    key: 'career_current_only',
                    sub_key: 'career_current_only',
                    model: null,
                    tooltip: {
                        title: 'Current role only',
                        content: [
                            { text: 'Only match current roles, industries, or companies.' },
                        ],
                    },
                    group: 'background',
                },
                */

                /*
                 * TODO: filters to be revisited at a later date.
                // === Business Collaborations ===
                'collaborations.include.values': {
                    label: 'Collaborations',
                    type: 'select-code-multiple',
                    key: 'collaborations.include.values',
                    model: null,
                    codeType: 'collaborations',
                    placeholder: 'Search collaborators',
                    multiplePlaceholder: 'Type to add collaborators',
                    noResultsText: 'All matches selected',
                    conjunction: {
                        key: 'collaborations.include.conjunction',
                        options: ['$and', '$or'],
                    },
                    tooltip: {
                        title: 'Collaborations',
                        content: [
                            { text: 'Organizations or partners the creator has collaborated with.' },
                        ],
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    group: 'business',
                },
                'collaborations.exclude.values': {
                    label: 'Exclude Collaborations',
                    type: 'select-code-multiple',
                    key: 'collaborations.exclude.values',
                    model: null,
                    codeType: 'collaborations',
                    placeholder: 'Search collaborators to exclude',
                    multiplePlaceholder: 'Type to add collaborators',
                    noResultsText: 'All matches selected',
                    conjunction: {
                        key: 'collaborations.exclude.conjunction',
                        options: ['$and', '$or'],
                    },
                    tooltip: {
                        title: 'Exclude Collaborations',
                        content: [
                            { text: 'Exclude creators who have collaborated with selected partners.' },
                        ],
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    group: 'business',
                },
                */

                'account_made_for_kids': {
                    label: 'Made for kids',
                    type: 'checkbox',
                    key: 'account_made_for_kids',
                    sub_key: 'account_made_for_kids',
                    model: null,
                    tooltip: {
                        title: 'Made for kids',
                        content: [
                            { text: 'Only show YouTube channels marked as "Made for Kids" through YouTube\'s audience setting.' },
                        ],
                    },
                    group: 'content',
                    conditions: () => ['youtube'].includes(this.platformId),
                },

                'audience_countries.include.values': {
                    label: 'Countries',
                    type: 'select-code-multiple',
                    key: 'audience_countries.include.values',
                    model: null,
                    codeType: 'audience_countries',
                    placeholder: 'Search for a country',
                    multiplePlaceholder: 'Type to add more countries',
                    noResultsText: 'All matches selected',
                    conjunction: {
                        key: 'audience_countries.include.conjunction',
                        options: ['$and', '$or']
                    },
                    specifics: [{
                        key: 'percent',
                        label: 'Audience percent',
                        type: 'number',
                        format : {
                            isPercentage: true,
                            decimals : 0,
                        },
                        model: {
                            min: 50,
                            max: 100,
                        },
                    }],
                    tooltip: {
                        title: 'Active audience countries',
                        content: [
                            {
                                text: 'Find creators whose active audience is located in specific countries based on platform analytics and AI analysis.'
                            }
                        ]
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    conditions: () => this.audienceFiltersEnabled,
                    group: 'audience'
                },

                'audience_languages.include.values': {
                    label: 'Languages',
                    type: 'select-code-multiple',
                    key: 'audience_languages.include.values',
                    model: null,
                    codeType: 'audience_languages',
                    placeholder: 'Search for a language',
                    multiplePlaceholder: 'Type to add more languages',
                    noResultsText: 'All matches selected',
                    conjunction: {
                        key: 'audience_languages.include.conjunction',
                        options: ['$and', '$or']
                    },
                    specifics: [{
                        key: 'percent',
                        label: 'Audience percent',
                        type: 'number',
                        format : {
                            isPercentage: true,
                            decimals : 0,
                        },
                        model: {
                            min: 50,
                            max: 100,
                        },
                    }],
                    tooltip: {
                        title: 'Active audience languages',
                        content: [
                            {
                                text: 'Find creators whose active audience primarily speaks specific languages based on platform analytics and AI analysis.'
                            }
                        ]
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    conditions: () => this.audienceFiltersEnabled,
                    group: 'audience'
                },

                'audience_gender_distribution.include.values': {
                    label: 'Genders',
                    type: 'select-code-multiple',
                    key: 'audience_gender_distribution.include.values',
                    model: null,
                    codeType: 'audience_genders',
                    placeholder: 'Search for a gender',
                    multiplePlaceholder: 'Type to add more genders',
                    noResultsText: 'All matches selected',
                    conjunction: {
                        key: 'audience_gender_distribution.include.conjunction',
                        options: ['$and', '$or']
                    },
                    specifics: [{
                        key: 'percent',
                        label: 'Audience percent',
                        type: 'number',
                        format : {
                            isPercentage: false,
                            decimals : 0,
                        },
                        model: {
                            min: 50,
                            max: 100,
                        },
                    }],
                    tooltip: {
                        title: 'Active audience genders',
                        content: [
                            {
                                text: 'Find creators whose active audience primarily identifies with specific genders based on platform analytics and AI analysis.'
                            }
                        ]
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: 'label',
                            direction: 'asc'
                        }
                    }),
                    conditions: () => this.audienceFiltersEnabled,
                    group: 'audience'
                },

                'audience_age_ranges.include.values': {
                    label: 'Age Ranges',
                    type: 'select-code-multiple',
                    key: 'audience_age_ranges.include.values',
                    model: null,
                    codeType: 'audience_age_ranges',
                    placeholder: 'Search for an age range',
                    multiplePlaceholder: 'Type to add more age ranges',
                    noResultsText: 'All matches selected',
                    tooltip: {
                        title: 'Active audience ages',
                        content: [
                            {
                                text: 'Find creators whose active audience primarily falls within specific age ranges based on platform analytics and AI analysis.',
                            },
                        ],
                    },
                    conjunction: {
                        key: 'audience_gender_distribution.include.conjunction',
                        options: ['$and', '$or']
                    },
                    specifics: [{
                        key: 'percent',
                        label: 'Audience percent',
                        type: 'number',
                        format : {
                            isPercentage: true,
                            decimals : 0,
                        },
                        model: {
                            min: 50,
                            max: 100,
                        },
                    }],
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    conditions: () => this.audienceFiltersEnabled,
                    group: 'audience',
                },

                'audience_ethnicity_distribution.include.values': {
                    label: 'Ethnicities',
                    type: 'select-code-multiple',
                    key: 'audience_ethnicity_distribution.include.values',
                    model: null,
                    codeType: 'audience_ethnicity_distribution',
                    placeholder: 'Search for an ethnicity',
                    multiplePlaceholder: 'Type to add more ethnicities',
                    noResultsText: 'All matches selected',
                    conjunction: {
                        key: 'audience_ethnicity_distribution.include.conjunction',
                        options: ['$and', '$or']
                    },
                    specifics: [{
                        key: 'percent',
                        label: 'Audience percent',
                        type: 'number',
                        format : {
                            isPercentage: true,
                            decimals : 0,
                        },
                        model: {
                            min: 50,
                            max: 100,
                        },
                    }],
                    tooltip: {
                        title: 'Active audience ethnicities',
                        content: [
                            {
                                text: 'Find creators whose active audience primarily identifies with specific ethnicities based on platform analytics and AI analysis.'
                            }
                        ]
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: 'label',
                            direction: 'asc'
                        }
                    }),
                    conditions: () => this.audienceFiltersEnabled,
                    group: 'audience'
                },

                'audience_education_level.include.values': {
                    label: 'Education levels',
                    type: 'select-code-multiple',
                    key: 'audience_education_level.include.values',
                    model: null,
                    codeType: 'audience_education_level',
                    placeholder: 'Search for an education level',
                    multiplePlaceholder: 'Type to add more education levels',
                    noResultsText: 'All matches selected',
                    conjunction: {
                        key: 'audience_education_level.include.conjunction',
                        options: ['$and', '$or']
                    },
                    specifics: [{
                        key: 'percent',
                        label: 'Audience percent',
                        type: 'number',
                        format : {
                            isPercentage: true,
                            decimals : 0,
                        },
                        model: {
                            min: 50,
                            max: 100,
                        },
                    }],
                    tooltip: {
                        title: 'Active audience education levels',
                        content: [
                            {
                                text: 'Find creators whose active audience primarily falls within specific education levels based on platform analytics and AI analysis.'
                            }
                        ]
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    conditions: () => this.audienceFiltersEnabled,
                    group: 'audience'
                },

                'audience_family_status.include.values': {
                    label: 'Family status',
                    type: 'select-code-multiple',
                    key: 'audience_family_status.include.values',
                    model: null,
                    codeType: 'audience_family_status',
                    placeholder: 'Search for a family status',
                    multiplePlaceholder: 'Type to add more family statuses',
                    noResultsText: 'All matches selected',
                    conjunction: {
                        key: 'audience_family_status.include.conjunction',
                        options: ['$and', '$or']
                    },
                    specifics: [{
                        key: 'percent',
                        label: 'Audience percent',
                        type: 'number',
                        format : {
                            isPercentage: true,
                            decimals : 0,
                        },
                        model: {
                            min: 50,
                            max: 100,
                        },
                    }],
                    tooltip: {
                        title: 'Active audience family status',
                        content: [
                            {
                                text: 'Find creators whose active audience primarily falls within specific family and relationship demographics based on platform analytics and AI analysis.'
                            }
                        ]
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: 'label',
                            direction: 'asc'
                        }
                    }),
                    conditions: () => this.audienceFiltersEnabled,
                    group: 'audience'
                },

                'audience_income_distribution.include.values': {
                    label: 'Income ranges',
                    type: 'select-code-multiple',
                    key: 'audience_income_distribution.include.values',
                    model: null,
                    codeType: 'audience_income_distribution',
                    placeholder: 'Search for an income level',
                    multiplePlaceholder: 'Type to add more income levels',
                    noResultsText: 'All matches selected',
                    conjunction: {
                        key: 'audience_income_distribution.include.conjunction',
                        options: ['$and', '$or']
                    },
                    specifics: [{
                        key: 'percent',
                        label: 'Audience percent',
                        type: 'number',
                        format : {
                            isPercentage: true,
                            decimals : 0,
                        },
                        model: {
                            min: 50,
                            max: 100,
                        },
                    }],
                    tooltip: {
                        title: 'Active audience income',
                        content: [
                            {
                                text: 'Find creators whose active audience primarily falls within specific income ranges based on platform analytics and AI analysis.'
                            }
                        ]
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: 'label',
                            direction: 'asc'
                        }
                    }),
                    conditions: () => this.audienceFiltersEnabled,
                    group: 'audience'
                },

                'audience_occupation_distribution.include.values': {
                    label: 'Occupations',
                    type: 'select-code-multiple',
                    key: 'audience_occupation_distribution.include.values',
                    model: null,
                    codeType: 'audience_occupation_distribution',
                    placeholder: 'Search for an occupation',
                    multiplePlaceholder: 'Type to add more occupations',
                    noResultsText: 'All matches selected',
                    conjunction: {
                        key: 'audience_occupation_distribution.include.conjunction',
                        options: ['$and', '$or']
                    },
                    specifics: [{
                        key: 'percent',
                        label: 'Audience percent',
                        type: 'number',
                        format : {
                            isPercentage: true,
                            decimals : 0,
                        },
                        model: {
                            min: 50,
                            max: 100,
                        },
                    }],
                    tooltip: {
                        title: 'Active audience occupations',
                        content: [
                            {
                                text: 'Find creators whose audience primarily works in specific occupations based on platform analytics and AI analysis.'
                            }
                        ]
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: `metrics.platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}`,
                            direction: 'desc',
                        }
                    }),
                    conditions: () => this.audienceFiltersEnabled,
                    group: 'audience'
                },

                /*
                 * TODO: filters to be revisited at a later date.
                'audience_engagement_style.include.values': {
                    label: 'Engagement Styles',
                    type: 'select-code-multiple',
                    key: 'audience_engagement_style.include.values',
                    model: null,
                    codeType: 'audience_engagement_style',
                    placeholder: 'Search for an engagement style',
                    multiplePlaceholder: 'Type to add more engagement styles',
                    noResultsText: 'All matches selected',
                    conjunction: {
                        key: 'audience_engagement_style.include.conjunction',
                        options: ['$and', '$or']
                    },
                    specifics: [{
                        key: 'percent',
                        label: 'Audience percent',
                        type: 'number',
                        format : {
                            isPercentage: true,
                            decimals : 0,
                        },
                        model: {
                            min: 50,
                            max: 100,
                        },
                    }],
                    tooltip: {
                        title: 'Audience Engagement Style',
                        content: [
                            {
                                text: 'How the audience typically interacts with the creator\'s content, categorized by behavior patterns.'
                            }
                        ]
                    },
                    displayOptions: () => ({
                        platform_id: this.platformId,
                        content_type: this.filtering.current.content_type,
                        metrics_type: 'creators',
                        sort: {
                            field: 'label',
                            direction: 'asc'
                        }
                    }),
                    conditions: () => this.audienceFiltersEnabled,
                    group: 'audience'
                },
                */

            //     audience_brand_safety: {
            //         label: 'Brand Safety Score',
            //         type: 'slider-range',
            //         key: 'audience_brand_safety',
            //         numberType: 'percent',
            //         dragValues: null,
            //         model: null,
            //         points: [
            //             {
            //                 value: 0,
            //                 step: 0.01
            //             },
            //             {
            //                 value: 0.1,
            //                 step: 0.01
            //             },
            //             {
            //                 value: 0.25,
            //                 step: 0.05
            //             },
            //             {
            //                 value: 1,
            //                 step: 0.1
            //             }
            //         ],
            //         maxDisplayValue: 1,
            //         tooltipFormatter: formatters.percent,
            //         inputFormatter: formatters.percent,
            //         tooltip: {
            //             title: 'Audience Brand Safety Score',
            //             content: [
            //                 {
            //                     text: `This score represents how brand-safe the audience's behavior and content interactions are, as determined by AI analysis.`
            //                 }
            //             ]
            //         },
            //         group: 'audience'
            //     },

            //     audience_cohesion_score: {
            //         label: 'Cohesion Score',
            //         type: 'slider-range',
            //         key: 'audience_cohesion_score',
            //         numberType: 'percent',
            //         dragValues: null,
            //         model: null,
            //         points: [
            //             {
            //                 value: 0,
            //                 step: 0.01
            //             },
            //             {
            //                 value: 0.1,
            //                 step: 0.01
            //             },
            //             {
            //                 value: 0.25,
            //                 step: 0.05
            //             },
            //             {
            //                 value: 1,
            //                 step: 0.1
            //             }
            //         ],
            //         maxDisplayValue: 1,
            //         tooltipFormatter: formatters.percent,
            //         inputFormatter: formatters.percent,
            //         tooltip: {
            //             title: 'Audience Cohesion Score',
            //             content: [
            //                 {
            //                     text: `This score measures how unified the creator's audience is in terms of interests and behaviors. Higher scores indicate a more focused and engaged community.`
            //                 }
            //             ]
            //         },
            //         group: 'audience'
            //     },

            //     audience_toxicity_score: {
            //         label: 'Toxicity Score',
            //         type: 'slider-range',
            //         key: 'audience_toxicity_score',
            //         numberType: 'percent',
            //         dragValues: null,
            //         model: null,
            //         points: [
            //             {
            //                 value: 0,
            //                 step: 0.01
            //             },
            //             {
            //                 value: 0.1,
            //                 step: 0.01
            //             },
            //             {
            //                 value: 0.25,
            //                 step: 0.05
            //             },
            //             {
            //                 value: 1,
            //                 step: 0.1
            //             }
            //         ],
            //         maxDisplayValue: 1,
            //         tooltipFormatter: formatters.percent,
            //         inputFormatter: formatters.percent,
            //         tooltip: {
            //             title: 'Audience Toxicity Score',
            //             content: [
            //                 {
            //                     text: `This score measures the level of negative or harmful interactions within the creator's audience. Lower scores indicate a healthier community.`
            //                 }
            //             ]
            //         },
            //         group: 'audience'
            //     }
            },

            // Populated on component creation
            changeListeners: {},
            barStyleCache : {},
        }
    },

    computed: {
        ...mapGetters(['getFromDictionary', 'selfdDiscoveryInstagramAudienceFiltersEnabled']),

        advancedFiltersModalSpecifics() {
            return this.getAdvancedFilterSpecifics(this.advancedFiltersModal.fieldKey)
        },

        // We only want to display fields that are enabled for the platform
        visibleFieldsByGroup() {
            const fieldGroups = _.reduce(
                this.fields,
                (results, field) => {

                    // Check if the fields custom visibility conditions have been met
                    const isPlatformFieldEnabled = field.conditions ? field.conditions() : true

                    if (isPlatformFieldEnabled) {
                        const isDirty =
                            !_.isEqual(_.get(this.filtering.current, field.key), _.get(this.filtering.default, field.key))

                        // console.log(field.key, isDirty, 'comparing', _.get(this.filtering.current, field.key), _.get(this.filtering.default, field.key))

                        const item = {
                            key: field.key,
                            type: field.type,
                            label: field.label,
                            is_enabled: isPlatformFieldEnabled,
                            title: field.title,
                            is_dirty: isDirty,
                            simple_view: [],
                        }

                        if (results[field.group]) {
                            results[field.group].fields.push(item)
                        } else {
                            results[field.group] = {
                                fields: [item],
                                simple_fields: [],
                            }
                        }
                    }

                    return results
                },
                {},
            )

            _.forEach(fieldGroups, (group, groupKey) => {
                _.forEach(group.fields, (field) => {
                    if (field.is_dirty || groupKey === 'aggregation') {
                        const currentValue = _.get(this.filtering.current, field.key)
                        const fieldConfig = this.fields[field.key]

                        let formatter = (value) => numeral(value).format('0,0')

                        if (fieldConfig.numberType === 'percent') {
                            formatter = (value) => numeral(value).format('0,0%')
                        }

                        if (['slider-range', 'slider-affinity'].includes(field.type)) {
                            if (currentValue.min > 0) {
                                group.simple_fields.push({
                                    key: field.key,
                                    label: field.label,
                                    text: `more than ${formatter(currentValue.min)}`,
                                })
                            }

                            if (currentValue.max < fieldConfig.maxDisplayValue) {
                                group.simple_fields.push({
                                    key: field.key,
                                    label: field.label,
                                    text: `less than ${formatter(currentValue.max)}`,
                                })
                            }
                        } else if (field.type === 'select-code-multiple') {
                            _.forEach(currentValue, (selectValue) => {
                                group.simple_fields.push({
                                    key: field.key,
                                    id: selectValue.id,
                                    label: field.label,
                                    text: `includes ${this.getCodeOptionDisplayLabel(fieldConfig, selectValue)}`,
                                })
                            })
                        } else if (field.type === 'hashtags-input') {
                            _.forEach(currentValue, (selectValue) => {
                                group.simple_fields.push({
                                    key: field.key,
                                    id: selectValue.id,
                                    label: field.label,
                                    text: `includes #${selectValue.id}`,
                                })
                            })
                        } else if (field.type === 'slider-single') {
                            group.simple_fields.push({
                                key: field.key,
                                label: field.label,
                                text: `at least ${currentValue}`,
                            })
                        } else if (field.type === 'dropdown-code-single') {
                            group.simple_fields.push({
                                key: field.key,
                                id: currentValue.id,
                                label: field.label,
                                text: `is ${voca.capitalize(currentValue)}`,
                            })
                        } else if (field.type === 'checkbox') {
                            group.simple_fields.push({
                                key: field.key,
                                label: field.label,
                                // text: `is ${currentValue}`,
                            })
                        } else if (field.type === 'radio-list') {
                            const currentOption = _.find(
                                fieldConfig.options,
                                (item) => String(item.id) === String(currentValue),
                            )

                            group.simple_fields.push({
                                key: field.key,
                                label: field.label,
                                text: `is ${currentOption?.title}`,
                                is_resetable: false,
                            })
                        }
                    }
                })
            })

            // console.log(`--- end computing visibleFieldsByGroup ---`)

            const mappedGroups = _.map(fieldGroups, (group, groupKey) => {
                return {
                    groupKey,
                    group
                }
            }).filter(({ groupKey }) => {
                if (this.groups[groupKey].condition) return this.groups[groupKey].condition()
                return true
            })

            return _.orderBy(mappedGroups, [({ groupKey }) => {
                return this.groups[groupKey]?.order
            }], [
                'asc'
            ])
        },

        dayRangeOptions() {
            const baseOptions = [
                { id: '30', title: '30 day view' },
                { id: '90', title: '90 day view' },
                { id: '365', title: '1 year view' },
            ]

            // Only show 3 year view for Twitch, YouTube, and TikTok
            if (['twitch', 'youtube', 'tiktok'].includes(this.platformId)) {
                baseOptions.push({ id: '1095', title: '3 year view' })
            }

            return baseOptions
        },

        audienceFiltersEnabled() {
            return ['twitch', 'youtube', 'tiktok'].includes(this.platformId) || (this.platformId === 'instagram' && this.selfdDiscoveryInstagramAudienceFiltersEnabled === true)
        },


    },

    watch: {
        platformId(newValue, oldValue) {
            if (newValue === oldValue) return

            // Update day range options based on platform
            this.fields.day_range.options = this.dayRangeOptions

            this.initializeRangeSliders()
            this.initializeChangeListeners()

            // Clear histogram data when platform changes
            this.histogramsByField = {}
            this.histogramLoadingFields = {}

            // Reload data for currently expanded group
            if (this.openFieldKey) {
                this.$nextTick(() => {
                    this.loadDataForExpandedGroup(this.openFieldKey)
                })
            }

            this.generateStyleCache(true)
        },

        filtering: {
            handler(newValue, oldValue) {
                this.setFilterLabels()
                // this.fields['content_count.min'].label = this.currentContentType()
                // console.log(`Filtering changed`, _.cloneDeep({ newValue, oldValue }))

                _.forEach(this.fields, (field, key) => {
                    const isPlatformFieldEnabled = _.has(this.filtering.options, field.key)

                    if (['content_type'].includes(field.key)) {
                        this.fields[key].options = this.filtering.options[key]
                    }

                    // Only update values for sliders that are shown
                    /* eslint-disable no-param-reassign */
                    if (isPlatformFieldEnabled) {
                        if (['slider-range', 'slider-affinity'].includes(field.type)) {
                            // Only update if the user is not dragging
                            if (!field.dragging) {
                                const lowerValue = _.get(this.filtering.current, field.key).min
                                const upperValue = Math.min(
                                    _.get(this.filtering.current, field.key).max,
                                    field.maxDisplayValue,
                                )
                                field.model = [lowerValue, upperValue]
                                field.localModel = [lowerValue, upperValue]
                                field.dragValues = [lowerValue, upperValue]

                                if (field.inputFormatter) {
                                    field.inputMin = this.formatRangeInputMin(field, lowerValue)
                                    field.inputMax = field.inputFormatter(upperValue, {
                                        max: field.maxDisplayValue,
                                        displayMax: field.maxDisplayValue,
                                    })
                                } else {
                                    field.inputMin = lowerValue
                                    field.inputMax = upperValue
                                }
                            }
                        }

                        if (field.type === 'slider-single') {
                            field.model = this.getSliderSingleModel(field)
                        }

                        if (field.type === 'select-code-multiple') {
                            field.model = _.get(this.filtering.current, field.key)
                        }

                        if (field.type === 'dropdown-code-single') {
                            field.model = _.get(this.filtering.current, field.key)
                        }

                        if (field.type === 'checkbox') {
                            field.model = _.get(this.filtering.current, field.key)
                        }

                        if (field.type === 'radio-list') {
                            field.model = _.get(this.filtering.current, field.key)
                        }

                        if (field.type === 'checkbox-affinity') {
                            field.model = _.get(this.filtering.current, field.key)
                        }

                        if (field.type === 'hashtags-input') {
                            field.model = _.get(this.filtering.current, field.key)
                        }
                    }
                    /* eslint-enable no-param-reassign */
                })
            },
            deep: true,
        },

        fields: {
            handler() {
                this.generateStyleCache()
            },
            deep: true,
        }
    },

    async created() {
        this.initializeRangeSliders()
        this.initializeChangeListeners()
        this.setFilterLabels()

        // Wrap the handler with lodash throttle at roughly 30fps (32ms)
        this.throttledDrag = _.throttle(this.throttledDrag.bind(this), 32)

        // Initialize day range options based on platform
        this.fields.day_range.options = this.dayRangeOptions

        // Remove histogram loading from here - will be lazy loaded
        this.generateStyleCache(true)
    },

    methods: {
        getCodeOptionDisplayLabel(field, item) {
            if (!item) return ''

            const itemId = String(item.id || '').toLowerCase()
            const itemLabel = String(item.label || '').toLowerCase()

            if (field?.codeType === 'alternate_platforms' && (itemId === 'twitter' || itemLabel === 'twitter')) {
                return 'X'
            }

            return item.label || item.id || ''
        },

        getAdvancedFiltersModalItemLabel() {
            return this.getCodeOptionDisplayLabel(
                this.fields[this.advancedFiltersModal.fieldKey],
                this.advancedFiltersModal.currentItem,
            )
        },

        getAdvancedFilterSpecifics(fieldKey) {
            const specifics = _.filter(
                this.fields[fieldKey]?.specifics || [],
                (specific) => !specific.disabled || specific.disabled() !== true,
            )

            if (fieldKey !== 'games_list.include.values') {
                return specifics
            }

            const primarySpecificKey = this.platformId === 'twitch' ? 'time_streamed_hours' : 'content_count'

            return specifics.map((specific) => ({
                ...specific,
                isAdvanced: specific.key !== primarySpecificKey,
            }))
        },

        activeSpecificsForItem(fieldKey, selectedItem) {
            const specifics = this.getAdvancedFilterSpecifics(fieldKey)
            if (!selectedItem || !selectedItem.sub_model) return []
            return specifics.filter((s) => {
                const sm = selectedItem.sub_model?.[s.key]
                if (!sm) return false
                const hasMin = sm.min !== null && sm.min !== undefined && sm.min !== ''
                const hasMax = sm.max !== null && sm.max !== undefined && sm.max !== ''
                const isTrueBoolean = s.type === 'boolean' && sm === true
                return hasMin || hasMax || isTrueBoolean
            })
        },
        toggleGroup(groupKey) {
            if (this.openFieldKey === groupKey) {
                // Closing the group
                this.openFieldKey = null
            } else {
                // Opening the group - do this immediately for instant UI feedback
                this.openFieldKey = groupKey

                // Load data in the background without blocking the UI expansion
                this.$nextTick(() => {
                    this.loadDataForExpandedGroup(groupKey)
                })
            }
        },

        openAdvancedFiltersModal(selectedItem, fieldKey) {
            // Set modal context
            this.advancedFiltersModal.currentItem = selectedItem
            this.advancedFiltersModal.fieldKey = fieldKey

            // Reset temp values to prevent cross-contamination between items
            this.advancedFiltersModal.tempValues = {}

            // Initialize temp values with current values for THIS item only
            const specifics = this.getAdvancedFilterSpecifics(fieldKey)

            if (!selectedItem.sub_model) {
                selectedItem.sub_model = {}
            }

            specifics.forEach((specificItem) => {

                if (specificItem?.type === 'boolean') {
                    this.advancedFiltersModal.tempValues[specificItem.key] = selectedItem.sub_model[specificItem.key]
                }
                else {
                    if (!selectedItem.sub_model[specificItem.key]) {
                        selectedItem.sub_model[specificItem.key] = { min: '', max: '' }
                    }
                    const currentMin = selectedItem.sub_model[specificItem.key]?.min ?? ''
                    const defaultMin = specificItem.inputMin !== undefined && specificItem.isAdvanced !== true && currentMin === ''
                        ? specificItem.inputMin
                        : currentMin

                    this.advancedFiltersModal.tempValues[specificItem.key] = {
                        min: defaultMin,
                        max: selectedItem.sub_model[specificItem.key]?.max ?? '',
                    }
                }

            })

            // Open after state is ready
            this.$nextTick(() => {
                this.advancedFiltersModal.isOpen = true
            })
        },

        closeAdvancedFiltersModal() {
            // Reset everything when closing (like resetState in EditListModal)
            this.advancedFiltersModal.isOpen = false
            this.advancedFiltersModal.currentItem = null
            this.advancedFiltersModal.fieldKey = null
            this.advancedFiltersModal.tempValues = {}
        },

        onAdvancedFiltersModalOpenChange(isOpen) {
            if (isOpen) {
                this.advancedFiltersModal.isOpen = true
                return
            }

            this.closeAdvancedFiltersModal()
        },

        applyAdvancedFilters() {
            if (!this.advancedFiltersModal.currentItem || !this.advancedFiltersModal.fieldKey) return

            const fieldKey = this.advancedFiltersModal.fieldKey
            const currentItem = this.advancedFiltersModal.currentItem
            const tempValues = this.advancedFiltersModal.tempValues

            const specifics = this.getAdvancedFilterSpecifics(fieldKey)

            // Ensure sub_model exists
            if (!currentItem.sub_model) currentItem.sub_model = {}

            // Build a cleaned sub_model containing only valid numeric constraints
            const cleanedSubModel = {}
            specifics.forEach((s) => {
                const raw = tempValues[s.key] || { min: '', max: '' }

                // Coerce values; treat empty string as null
                if (s.type === 'boolean') {
                    cleanedSubModel[s.key] = (raw === true)
                }
                else {
                    const toNumberOrNull = (v) => {
                        if (v === undefined || v === null || v === '') return null
                        const n = Number(v)
                        if (!Number.isFinite(n)) return null

                        return s.inputMin !== undefined ? Math.max(n, s.inputMin) : n
                    }

                    const minVal = toNumberOrNull(raw.min)
                    const maxVal = toNumberOrNull(raw.max)

                    // Only include keys that have at least one bound specified
                    if (minVal !== null || maxVal !== null) {
                        cleanedSubModel[s.key] = { min: minVal, max: maxVal }
                    }
                }
            })

            // Assign cleaned sub_model back to the item (or remove if empty)
            if (Object.keys(cleanedSubModel).length > 0) {
                currentItem.sub_model = cleanedSubModel
            } else if (currentItem.sub_model) {
                delete currentItem.sub_model
            }

            // Ensure the parent component knows filters have changed since we're modifying nested properties.
            this.$emit('filter-change-single', {
                key: fieldKey,
                value: this.fields[fieldKey].model,
            })

            this.closeAdvancedFiltersModal()
        },

        async loadDataForExpandedGroup(groupKey) {
            const group = this.visibleFieldsByGroup.find(g => g.groupKey === groupKey)
            if (!group) return

            // Collect histogram fields that need loading
            const histogramFields = []

            // Process each field in the group
            for (const field of group.group.fields) {
                // Handle CodeSelect components (multiselect and dropdown-single)
                if (this.fields[field.key]?.type === 'select-code-multiple' || this.fields[field.key]?.type === 'dropdown-code-single') {
                    // Find the CodeSelect component and preload its options
                    const codeSelectRefs = this.$refs[`codeselect-${field.key}`]
                    if (codeSelectRefs && codeSelectRefs.length > 0) {
                        const codeSelectRef = codeSelectRefs[0]
                        // Load options based on component type
                        if (this.fields[field.key]?.type === 'dropdown-code-single') {
                            // For dropdown-single, use the dedicated loading method
                            if (codeSelectRef && typeof codeSelectRef.loadOptionsForDropdownSingle === 'function') {
                                try {
                                    await codeSelectRef.loadOptionsForDropdownSingle()
                                } catch (error) {
                                    console.error('Error preloading dropdown-single options:', error)
                                }
                            }
                        } else if (this.fields[field.key]?.type === 'select-code-multiple') {
                            // For multiselect, refresh the multiselect options to display them
                            if (codeSelectRef?.$refs?.multiselect) {
                                try {
                                    codeSelectRef.$refs.multiselect.refreshOptions()
                                } catch (error) {
                                    console.error('Error preloading multiselect options:', error)
                                }
                            }
                        }
                    }
                }

                // Collect histogram fields for batch loading
                if (this.fields[field.key]?.type === 'slider-range') {
                    histogramFields.push(field.key)
                }
            }

            // Load histograms in batch if any slider-range fields exist
            if (histogramFields.length > 0) {
                try {
                    // Mark fields as loading and generate placeholder bars
                    histogramFields.forEach(fieldKey => {
                        this.histogramLoadingFields[fieldKey] = true
                    })

                    // Generate placeholder bars for loading histograms
                    this.generatePlaceholderBars(histogramFields)

                    const histogramData = await this.$store.dispatch('fetchHistogramBatch', {
                        platformId: this.platformId,
                        fieldKeys: histogramFields
                    })

                    // Clear loading states
                    histogramFields.forEach(fieldKey => {
                        delete this.histogramLoadingFields[fieldKey]
                    })

                    // Merge the loaded histogram data with existing data
                    this.histogramsByField = { ...this.histogramsByField, ...histogramData }

                    // Force regeneration of style cache for the newly loaded histograms
                    this.generateStyleCache(true)
                } catch (error) {
                    // Clear loading states on error
                    histogramFields.forEach(fieldKey => {
                        delete this.histogramLoadingFields[fieldKey]
                    })
                    console.error('Error loading histogram batch:', error)
                }
            }
        },


        async getHistograms() {
            return Promise.props(
                _.reduce(this.fields, (promises, field, fieldKey) => {
                    if (field.type === 'slider-range') {
                        promises[fieldKey] = this.$axios.get(`/discovery/${this.platformId}/histograms/${fieldKey}`).then(({ data }) => {
                            if (data.payload.histogram) {
                                return data.payload.histogram
                            }

                            return null
                        }).catch((error) => {
                            console.error(`[${fieldKey}] Error fetching histogram`, error)
                        })
                    }

                    return promises
                }, {})
            )
        },

        onRemoveItemFromList(field, selectedItem) {
            field.model = _.filter(field.model, item => item.id !== selectedItem.id)
        },

        // Use Intl.NumberFormat for formatting with commas.
        formatNumber(value) {
            return new Intl.NumberFormat('en-US').format(value)
        },

        // Remove commas before converting back to a number.
        parseNumber(valueStr, field) {
            if (field.numberType === 'percent') {
                return Number(valueStr.replace(/[,+%]/g, '')) / 100
            }

            return Number(valueStr.replace(/[,+]/g, ''))
        },

        formatRangeInputMin(field, value, max = field.maxDisplayValue) {
            if (field.minInputLabel && value === 0) {
                return field.minInputLabel
            }

            if (field.inputFormatter) {
                return field.inputFormatter(value, {
                    max,
                    displayMax: max,
                })
            }

            return value
        },

        onMinBlur(key) {
            const field = this.fields[key]

            const raw = this.parseNumber(field.inputMin, field)
            const newVal = Math.min(raw, field.model[1])

            // console.log(key, raw, newVal, field)

            field.localModel[0] = newVal
            field.model[0] = newVal
            field.dragValues[0] = newVal

            if (field.inputFormatter) {
                field.inputMin = this.formatRangeInputMin(field, newVal)
            } else {
                field.inputMin = newVal
            }

            // update only this field in filtering.current
            this.setSingleValue(key, { min: newVal, max: field.model[1], ignoreMax : field.model[1] === field.maxDisplayValue })
        },

        onMaxBlur(key) {
            const field = this.fields[key]
            const raw = this.parseNumber(field.inputMax, field)
            const newVal = Math.min(Math.max(raw, field.model[0]), field.maxDisplayValue)

            // console.log(key, raw, newVal, field)

            field.localModel[1] = newVal
            field.model[1] = newVal
            field.dragValues[1] = newVal

            if (field.inputFormatter) {
                field.inputMax = field.inputFormatter(newVal, {
                    max: field.maxDisplayValue,
                    displayMax: field.maxDisplayValue,
                })
            } else {
                field.inputMax = newVal
            }

            // console.log(field.localModel[1], field.model[1], field.dragValues[1], field.inputMax)

            this.setSingleValue(key, { min: field.model[0], max: newVal, ignoreMax : newVal === field.maxDisplayValue })
        },

        // Debounced input handler for the lower bound.
        onMinInput(e, key) {
            // Clear any pending timeout so we only update once the user pauses typing.
            // clearTimeout(this.fields[key].minTimeout)
            this.fields[key].inputMin = e.target.value
        },

        onMaxInput(e, key) {
            // clearTimeout(this.fields[key].maxTimeout)
            this.fields[key].inputMax = e.target.value
        },

        throttledDrag(fieldKey, values) {
            const field = this.fields[fieldKey]
            if (!Array.isArray(values)) {
                console.warn('Expected an array for drag values, but got:', values)
                return
            }

            // Mark that we’re dragging
            field.dragging = true

            field.localModel = [...values]
            field.model = [...values]
            field.dragValues = [...values]
            const maxVal = field.maxDisplayValue || (field.slider && field.slider.max)

            if (field.inputFormatter instanceof Function) {
                field.inputMin = this.formatRangeInputMin(field, values[0], maxVal)
                field.inputMax = field.inputFormatter(values[1], { max: maxVal, displayMax: maxVal })
            } else {
                field.inputMin = values[0]
                field.inputMax = values[1]
            }

            // this.setSingleValue(fieldKey, field.model)
        },

        async generateStyleCache(flush) {
            _.forEach(this.fields, field => {
                if (field.type === 'slider-range') {
                    const groupFieldKey = `${field.group}-${field.key}`
                    const dragPositionKey = field.localModel ? `${field.localModel[0]}-${field.localModel[1]}` : ''

                    if (this.barStyleCache[groupFieldKey]?.dragPositionKey !== dragPositionKey || flush === true) {
                        const cachedValues = []
                        const path = `platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}.buckets`
                        const values = this.$lodash.get(this.histogramsByField[field.key], path)

                        if(values) {
                            _.forEach(values, (value, index) => {
                                cachedValues[index] = this.barStyle(index+1, field.localModel, field.key)
                            })
                        }

                        this.barStyleCache[groupFieldKey] = {
                            dragPositionKey,
                            values : cachedValues,
                            isPlaceholder: false
                        }
                    }
                }
            })
        },

        generatePlaceholderBars(fieldKeys) {
            fieldKeys.forEach(fieldKey => {
                const field = this.fields[fieldKey]
                if (field && field.type === 'slider-range') {
                    const groupFieldKey = `${field.group}-${fieldKey}`
                    const dragPositionKey = field.localModel ? `${field.localModel[0]}-${field.localModel[1]}` : ''

                    // Generate random placeholder bars (typically 30-50 bars for histograms)
                    const numBars = 40 + Math.floor(Math.random() * 10) // 40-50 bars
                    const placeholderBars = []

                    for (let i = 0; i < numBars; i++) {
                        // Generate random heights between 3px and 25px
                        const height = Math.floor(Math.random() * 22) + 3
                        placeholderBars.push({
                            backgroundColor: '#e2e8f0', // Light gray
                            height: `${height}px`,
                            flex: '1',
                            opacity: '0.7',
                            animation: 'pulse 2s infinite'
                        })
                    }

                    // Set placeholder bars in the cache
                    this.barStyleCache[groupFieldKey] = {
                        dragPositionKey,
                        values: placeholderBars,
                        isPlaceholder: true
                    }
                }
            })
        },

        isHistogramLoading(fieldKey) {
            return Boolean(this.histogramLoadingFields[fieldKey])
        },


        barStyle(index, dragValues, fieldKey) {
            // Get histogram data
            const histogramData = this.$lodash.get(
                this.histogramsByField[fieldKey],
                `platforms.${this.platformId}.${this.filtering.current.content_type}.day_${this.filtering.current.day_range}.buckets`
            )

            if (!histogramData || histogramData.length === 0 || !histogramData[index - 1]) {
                return { backgroundColor: '#f1f5f9', height: '5px', flex: '1' }
            }

            const bucket = histogramData[index - 1]

            // Find the max percentage in all buckets
            const maxPercent = Math.max(...histogramData.map(b => b.percent))

            // Scale height relative to the max percentage (max will be 40px)
            const heightScale = maxPercent > 0 ? 40 / maxPercent : 1
            const height = Math.max(3, bucket.percent * heightScale)

            // Check if bucket is within the selected range
            const isActive = !(dragValues[0] > bucket.start || dragValues[1] < bucket.end)

            return {
                backgroundColor: isActive ? '#A5B4FC' : '#f1f5f9',
                height: `${height}px`,
                flex: '1'
            }
        },

        resetFilters(fieldKeys) {
            _.forEach(fieldKeys, (fieldKey) => {
                this.setSingleValue(fieldKey, _.get(this.filtering.default, fieldKey))
            })
        },

        getSliderSingleModel(field) {
            const value = _.get(this.filtering.current, field.key)
            const min = field.slider?.min

            if (min != null && (value == null || value < min)) {
                return min
            }

            return value
        },

        hasSelectedGamesForMatchedCountFilter() {
            return _.size(_.get(this.filtering.current, 'games_list.include.values', [])) > 0
        },

        getSliderSingleFilterValue(field, value) {
            if (field.key === 'games_list_matched_count.min' && !this.hasSelectedGamesForMatchedCountFilter()) {
                return _.get(this.filtering.default, field.key)
            }

            if (field.slider?.min != null && value != null) {
                return Math.max(value, field.slider.min)
            }

            return value
        },

        removeItemFromFilterById(fieldKey, id) {

            const currentValues = _.get(this.filtering.current, fieldKey)
            const defaultValues = _.get(this.filtering.default, fieldKey)

            if (Array.isArray(currentValues)) {
                this.setSingleValue(fieldKey, _.filter(currentValues, (currentItem) => {
                    return currentItem.id !== id
                }))
            }
            else {
                this.setSingleValue(fieldKey, defaultValues)
            }

        },

        setSingleValue(key, value) {
            _.set(this.filtering.current, key, value)

            this.$emit('filter-change-single', {
                key,
                value,
            })
        },

        // TODO: Remove debounce on non-slider fields
        initializeChangeListeners() {
            _.forEach(this.fields, (field, key) => {
                const handleNewValue = (newValue) => {

                    // For slider-range and slider-affinity, commit the local value
                    if (['slider-range', 'slider-affinity'].includes(field.type)) {
                        // console.log(`initializeChangeListeners:`, newValue, field.localModel)

                        field.model = [...field.localModel]
                        field.dragging = false
                        const emittedEvent = { key, value: null }

                        if (field.type === 'slider-range') {
                            emittedEvent.value = {
                                min: field.localModel[0],
                                max: field.localModel[1],
                            }
                        } else if (field.type === 'slider-affinity') {
                            if (field.localModel[0] === field.localModel[1]) {
                                if (field.localModel[0] === 0) {
                                    emittedEvent.value = {
                                        min: Math.round(field.localModel[0] * 100) / 100,
                                        max: Math.round((field.localModel[1] + 0.6) * 100) / 100,
                                    }
                                    field.model = [emittedEvent.value.min, emittedEvent.value.max]
                                } else {
                                    emittedEvent.value = {
                                        min: Math.round((field.localModel[0] - 0.1) * 100) / 100,
                                        max: Math.round(field.localModel[1] * 100) / 100,
                                    }
                                }
                            } else {
                                emittedEvent.value = {
                                    min: Math.round(field.localModel[0] * 100) / 100,
                                    max: Math.round(field.localModel[1] * 100) / 100,
                                }
                            }
                        }
                        emittedEvent.value.ignoreMax = emittedEvent.value.max === field.maxDisplayValue

                        this.$emit('filter-change-single', emittedEvent)

                    } else if (field.type === 'drop-down-source') {
                        this.$emit('filter-change-single', { key, value: newValue?.value?.id ? [newValue?.value?.id] : [] })
                    } else if (field.type === 'select-code-multiple') {

                        _.forEach(newValue, (newItem) => {

                            if (!newItem.sub_model) {
                                newItem.sub_model = {}

                                _.forEach(field.specifics, (item) => {
                                    newItem.sub_model[item.key] = _.cloneDeep(item.model)
                                })
                            }
                        })

                        this.$emit('filter-change-single', { key, value: newValue })

                    } else if (field.type === 'slider-single') {
                        let eventKey = key
                        if (field.sub_key) {
                            eventKey = `${field.key}.${field.sub_key}`
                        }
                        this.$emit('filter-change-single', {
                            key: eventKey,
                            value: this.getSliderSingleFilterValue(field, newValue),
                        })
                    } else if (
                        field.type === 'checkbox' ||
                        field.type === 'radio-list' ||
                        field.type === 'checkbox-affinity'
                    ) {
                        this.$emit('filter-change-single', { key, value: this.fields[field.key].model })
                        // Special handling: when toggling primary industry only, also set hierarchy.index
                        if (key === 'classifications.primary_only') {
                            const isPrimary = this.fields[field.key].model === true
                            const hasHierarchySelection = !!this.$lodash.get(this.filtering.current, 'classifications.hierarchy.topic') || !!this.$lodash.get(this.filtering.current, 'classifications.hierarchy.category')
                            const indexKey = 'classifications.hierarchy.index'
                            const indexValue = isPrimary && hasHierarchySelection ? 0 : null
                            this.$emit('filter-change-single', { key: indexKey, value: indexValue })
                            this.$emit('filter-change-single', { key: 'classifications.primary_only', value: isPrimary })
                        }
                    } else if (field.type === 'dropdown-code-single') {
                        this.$emit('filter-change-single', { key, value: newValue })
                        // When selecting niche/category, ensure index follows the primary-only toggle
                        if ((key === 'classifications.hierarchy.topic' || key === 'classifications.hierarchy.category') && newValue !== null && newValue !== undefined) {
                            const isPrimaryFlag = this.$lodash.get(this.filtering.current, 'classifications.primary_only')
                            const enforcePrimary = isPrimaryFlag === true
                            const indexKey = 'classifications.hierarchy.index'
                            const indexValue = enforcePrimary ? 0 : null
                            this.$emit('filter-change-single', { key: indexKey, value: indexValue })
                        }

                        if ((key === 'classifications.hierarchy.topic' || key === 'classifications.hierarchy.category')) {
                            if (newValue === null || newValue === undefined) {
                                this.$emit('filter-change-single', { key: 'classifications.primary_only', value: false })
                            }
                            else {
                                this.$emit('filter-change-single', { key: 'classifications.primary_only', value: this.$lodash.get(this.filtering.current, 'classifications.primary_only') })
                            }
                        }

                        if (key === 'classifications.hierarchy.topic' && (newValue === null || newValue === undefined)) {
                            const indexKey = 'classifications.hierarchy.index'
                            this.$emit('filter-change-single', { key: indexKey, value: null })
                        }
                    } else if (field.type === 'hashtags-input') {
                        this.$emit('filter-change-single', { key, value: newValue })
                    }
                }

                // We only want to debounce slider values
                if (['slider-range', 'slider-single'].includes(field.type)) {
                    this.changeListeners[key] = _.debounce(handleNewValue, 250)
                } else {
                    this.changeListeners[key] = handleNewValue
                }

                // For non-slider fields, initialize the model from filtering.current
                if (!['slider-range', 'slider-affinity'].includes(field.type)) {
                    field.model = field.type === 'slider-single'
                        ? this.getSliderSingleModel(field)
                        : _.get(this.filtering.current, field.key)
                }
            })
        },

        initializeRangeSliders() {
            _.forEach(this.fields, (field, key) => {
                const isPlatformFieldEnabled = _.has(this.filtering.options, field.key)
                const currentFieldValue = _.get(this.filtering.current, key)

                if (['slider-range', 'slider-affinity'].includes(field.type) && isPlatformFieldEnabled) {
                    const minValue = currentFieldValue.min
                    const maxValue = Math.min(field.maxDisplayValue, currentFieldValue.max)
                    // set both the “committed” value and the local copy
                    this.fields[key].model = [minValue, maxValue]
                    this.fields[key].localModel = [minValue, maxValue]
                    this.fields[key].dragValues = [minValue, maxValue]

                    if (field.inputFormatter) {
                        field.inputMin = this.formatRangeInputMin(field, minValue)
                        field.inputMax = field.inputFormatter(maxValue, {
                            max: field.maxDisplayValue,
                            displayMax: field.maxDisplayValue,
                        })
                    } else {
                        field.inputMin = minValue
                        field.inputMax = maxValue
                    }

                    const { points } = _.get(this.fields, key)
                    const result = []

                    if (points && points.length) {
                        points.forEach((point, idx) => {
                            const lastPointValue = points[points.length - 1].value

                            if (point.value !== lastPointValue) {
                                const nextPoint = points[idx + 1]
                                for (let i = point.value; i <= nextPoint.value; i += point.step) {
                                    result.push(Math.round(i * 100) / 100)
                                }
                            }
                        })
                    }

                    const uniqueValues = new Set(result)
                    if (!uniqueValues.has(field.maxDisplayValue)) {
                        uniqueValues.add(field.maxDisplayValue)
                    }

                    const markValues = _.map(points, 'value')
                    this.fields[key].data = [...uniqueValues]

                    if (this.fields[key].marks) {
                        this.fields[key].marks = (value) => {
                            if (markValues.includes(value)) {
                                return {
                                    label: this.$filters.formatWholeNumberWithUnit(value),
                                }
                            }
                            return false
                        }
                    }

                    // this.fields[key].barHeights = Array.from(
                    //     { length: 37 },
                    //     () => `h-${Math.round(Math.random() * 8 + 2)}`,
                    // )
                }
            })
        },

        setFilterLabels() {
            this.fields.sum_content_items.label = 'Minimum posts'
            this.fields.followers.label = this.platformId === 'youtube' ? 'Subscribers' : 'Followers'
            this.fields.followers.tooltip.title = this.platformId === 'youtube' ? 'Total subscriber count' : 'Total follower count'
            this.fields.followers.tooltip.content = [
                {
                    text: this.platformId === 'youtube'
                        ? 'Current subscriber count on YouTube.'
                        : 'Current follower count on the selected platform.',
                },
            ]
        },

        validateInput(event, itemConfig, model, field) {

            // Edge case when input is numeric, 15. will have an event.target.value of '15' not '15.'

            if (!itemConfig?.format || event.target.value.trim() === '' || itemConfig?.type !== 'number') {
                if (model[field].toString() !== event.target.value.toString()) {
                    model[field] = event.target.value
                }
                return
            }

            if (/^\d*\.?\d*$/.test(event.target.value)) {

                const parsedNumber = parseFloat(event.target.value)

                if (!parsedNumber) {
                    event.target.value = model[field]
                }
                else {

                    const beforeValue = parsedNumber.toString()
                    const afterValue = parseFloat(parsedNumber.toFixed(itemConfig?.format?.decimals)).toString()

                    if (itemConfig?.format?.decimals >= 0 && beforeValue !== afterValue) {
                        event.target.value = afterValue
                        if (model[field]?.toString() !== event.target.value.toString()) {
                            model[field] = event.target.value
                        }
                    }
                    else if (model[field]?.toString() !== event.target.value.toString()) {
                            model[field] = event.target.value
                        }
                }
            }
            else {
                event.target.value = model[field]
            }
        },

        validateInputKeyPress(event, itemConfig) {
            if (!itemConfig?.format || itemConfig?.type !== 'number') {
                return
            }

            // '-' sign is charCode 45 if we need it
            const charCode = (event.which) ? event.which : event.keyCode
            if ((charCode > 31 && (charCode < 48 || charCode > 57)) && !(charCode === 46 && itemConfig?.format?.decimals > 0)) {
                event.preventDefault()
            }
        },

        dotOptionsForRange(key) {
            const model = this.fields[key].model
            const min = Math.min(...this.fields[key].data)
            const max = this.fields[key].maxDisplayValue

            const [left, right] = model

            const atMin = left === min && right === min
            const atMax = left === max && right === max

            return [
                {
                    disabled: atMin,
                },
                {
                    disabled: atMax,
                },
            ]
        }
    },
}
</script>

<style lang="scss">
.merge-tooltip-bottom {
    position: absolute;
    left: 50%;
    top: 100%;
    transform: translate(-50%, 15px);
    z-index: 50;
}

/* This is so that merge tooltips show above marks */
.vue-slider-marks {
    .vue-slider-mark {
        z-index: -1;
    }
}

/* Needed for overlapping range sliders at max value */
.vue-slider-dot {
  z-index: 5 !important;
  pointer-events: auto;
}

.vue-slider-dot.vue-slider-dot-disabled {
    z-index: 1 !important;
    pointer-events: none;
}
</style>
